<template>
  <DialogWrapper
    ref="inviteHmInterviewDialog"
    class="invite-hm-interview-dialog"
    destroyOnClose
    :show.sync="show"
    :loading="loading"
    :closeOnClickModal="false"
    @closed="handleClosedDialog"
  >
    <div class="hm-interview-form">
      <Step :stepList="stepList" :activeIndex="activeIndex"></Step>
      <StepContent class="step-content" :stepList="stepList" :activeIndex="activeIndex">
        <template #hmSelector>
          <div v-rect-loading="stepLoading" class="content">
            <div v-if="showInterviewModel" class="interview-model">
              <div class="hm-model interview-model-panel">
                <div class="title">{{ $t(`${i18nPrefix}.title.hiringManager`)}}</div>
                <el-radio-group v-model="isSingleHm" @input="handleSingleHmChange">
                  <el-radio :label="true">{{ $t(`${i18nPrefix}.label.single`)}}</el-radio>
                  <el-radio :label="false">{{ $t(`${i18nPrefix}.label.multiple`)}}</el-radio>
                </el-radio-group>
              </div>
              <div v-if="showGroupInterviewOption" class="interview-type interview-model-panel">
                <div class="title">{{ $t(`${i18nPrefix}.title.interviewType`)}}</div>
                <div class="interview-type-content">
                  <el-radio-group v-model="isGroupInterview">
                    <el-radio :label="false">{{ $t(`${i18nPrefix}.label.individual`)}}</el-radio>
                    <el-radio :label="true">{{ $t(`${i18nPrefix}.label.groupInterviewType`)}}</el-radio>
                  </el-radio-group>
                  <el-input
                    v-show="isGroupInterview"
                    v-model="groupInterviewName"
                    class="group-name-input"
                    :class="{ 'is-error': groupInterviewNameError }"
                    :placeholder="$t(`${i18nPrefix}.placeholder.groupInterviewName`)"
                    @input="handleGroupInterviewNameInput"
                  ></el-input>
                </div>
              </div>
              <div
                v-if="showAutomatedSchedulerOption"
                class="automate interview-model-panel"
                :class="{ 'is-full-row': showGroupInterviewOption }"
              >
                <div class="title">{{ $t(`${i18nPrefix}.title.automate`)}}</div>
                <el-checkbox v-model="isAutomate">{{ $t(`${i18nPrefix}.label.enableAutomate`)}}</el-checkbox>
              </div>
            </div>
            <div class="hm">
              <div class="title content-title">
                <span>{{ $t(`${i18nPrefix}.title.selectHiringManager`) }}</span>
                <i v-auto-tooltip.always="$t(`${i18nPrefix}.tip.selectHiringManager`)" class="iconfont icona-exclamationmark icon-tip"></i>
              </div>
              <HmSelector
                class="hm-selector"
                ref="hmSelector"
                hiddenLoading
                hiddenHeaderMessage
                :onlySelectOne="isSingleHm"
                :hiddenSelectedHM="selectHiringList.length === 0"
                :applicationList="applicationList"
                :defaultSelectedHmList="selectHiringList"
                @changeHmList="handleHmListChange"
                @selectHm="handleSelectHm"
              >
              </HmSelector>
            </div>
            <div v-if="!isAutomatedSchedulerActive" class="time">
              <div v-if="hasAutomatedSchedulerPremium" class="title content-title">{{ $t(`${i18nPrefix}.title.selectInterviewTimeRange`) }}</div>
              <div class="time-type-content">
                <InterviewDuration v-model="duration" ref="interviewDuration" @change="handleDurationChange"/>
                <div class="time-zone">
                  <span class="label">{{ $t(`${i18nPrefix}.label.timeZone`) }}</span>
                  <el-select class="time-zone-selector" v-model="timeZone" filterable :placeholder="$t(`${i18nPrefix}.placeholder.selectTimeZone`)" @change="handleTimezoneChange">
                    <el-option
                      v-for="item in timeZoneList"
                      :key="item.id"
                      :label="item.value"
                      :value="item.key"
                    >
                    </el-option>
                  </el-select>
                </div>
              </div>
              <div v-if="selectHiringList.length > 0 || showInterviewSchedule" class="time-schedule-content">
                <InterviewCalendar v-model="selectedDate" :selectedInterviewDateList="selectedDateList"/>
                <InterviewSchedule
                  :selectedDate="selectedDate"
                  :selectedDateTimeList="selectedDateTimeList"
                  :hmScheduleList="hmScheduleList"
                  :durationMinutes="durationMinutes"
                  @selectSchedule="handleSelectSchedule"
                />
              </div>
              <div class="time-range-content">
                <InterviewTimeRange
                  v-for="(interviewDateTime, index) in selectedDateTimeList"
                  ref="interviewDateTimeRange"
                  :key="interviewDateTime.vid"
                  :index="index"
                  :durationMinutes="durationMinutes"
                  :errorMessage="interviewDateTime.duplicate ? $t(`${i18nPrefix}.message.duplicateDateTime`) : ''"
                  :interviewDateTime="interviewDateTime"
                  @deleteTime="handleDeleteTime(index)"
                  @change="handleTimeRangeChange(index, $event)"
                  @endTimeClick="handleEndTimeClick"
                />
              </div>
            </div>
            <div class="location">
              <div class="title content-title">{{ $t(`${i18nPrefix}.title.selectLocation`) }}</div>
              <div class="location-select">
                <el-radio-group v-model="interviewType" @input="handleInterviewTypeChange">
                  <el-radio-button
                    v-for="item in interviewTypeList"
                    :key="item.key"
                    :label="item.key"
                  >
                    {{ item.label }}
                  </el-radio-button>
                </el-radio-group>
                <div v-if="interviewType === INTERVIEW_TYPE_TELEPHONE" class="telephone-tip">{{ $t(`${i18nPrefix}.label.telephoneTip`) }}</div>
                <div
                  v-if="interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING || interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING"
                  class="teams-meeting"
                  :class="((interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING && teamsMeetingUrl) || (interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING && zoomMeetingUrl)) && 'has-teams-url'"
                >
                  <template v-if="interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING && teamsMeetingUrl">
                    <el-input v-model="teamsMeetingUrl" disabled></el-input>
                    <el-input v-model="teamsMeetingId" disabled></el-input>
                    <el-input v-model="teamsMeetingPassword" disabled></el-input>
                  </template>
                  <template v-else-if="interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING && zoomMeetingUrl">
                    <el-input v-model="zoomMeetingUrl" disabled></el-input>
                    <el-input v-model="zoomMeetingId" disabled></el-input>
                    <el-input v-model="zoomMeetingPassword" disabled></el-input>
                  </template>
                  <span v-else>{{ interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING ? $t(`${i18nPrefix}.label.zoomMeeting`) : $t(`${i18nPrefix}.label.teamsMeeting`) }}</span>
                </div>
                <template v-if="interviewMeetingRoomList.length > 0">
                  <el-select
                    v-model="interviewMeetingRoom"
                    class="meeting-room"
                    popper-class="meeting-room-options"
                    filterable
                    clearable
                    default-first-option
                    :placeholder="$t(`${i18nPrefix}.placeholder.selectMeetingRoom`)"
                    @change="handleInterviewMeetingRoomChange"
                    @clear="handleClearInterviewMeetingRoom"
                  >
                    <el-option
                      v-for="(interviewMeetingRoom, key) in interviewMeetingRoomList"
                      :key="key"
                      :label="interviewMeetingRoom.i18nName"
                      :value="interviewMeetingRoom.id"
                    >
                      <i v-if="interviewMeetingRoom.email" class="time-icon iconfont iconshijian-mian" :class="interviewMeetingRoom.isBusy && 'is-busy'"></i>
                      <span class="meeting-room-label">{{ interviewMeetingRoom.i18nName }}</span>
                    </el-option>
                  </el-select>
                  <div v-if="interviewMeetingRoomError" class="error">{{ interviewMeetingRoomError }}</div>
                </template>
                <template v-if="interviewType === INTERVIEW_TYPE_F2F">
                  <el-select
                    v-model="location"
                    filterable
                    clearable
                    allow-create
                    default-first-option
                    ref="locationSelectRef"
                    :placeholder="$t(`${i18nPrefix}.placeholder.selectLocation`)"
                    @change="handleLocationChange"
                  >
                    <el-option
                      v-for="(location, key) in locationList"
                      :key="key"
                      :label="location.value"
                      :value="location"
                    ></el-option>
                  </el-select>
                  <div v-if="locationError" class="error">{{ locationError }}</div>
                </template>
                <template v-if="interviewType === INTERVIEW_TYPE_TELEPHONE">
                  <el-input class="telephone-input" v-model="telephone" :placeholder="$t(`${i18nPrefix}.placeholder.inputTelephone`)"></el-input>
                  <div v-show="telephoneError" class="error">{{ telephoneError }}</div>
                </template>
                <template v-if="interviewType === INTERVIEW_TYPE_ONLINE">
                  <el-input class="url online-input" v-model="onlineInfo.url" :placeholder="$t(`${i18nPrefix}.placeholder.onlineInterview.url`)"></el-input>
                  <div v-show="urlError" class="error">{{ urlError }}</div>
                  <el-input class="meeting-id online-input" v-model="onlineInfo.meetingId" :placeholder="$t(`${i18nPrefix}.placeholder.onlineInterview.meetingId`)"></el-input>
                  <el-input class="meeting-password online-input" v-model="onlineInfo.meetingPassword" :placeholder="$t(`${i18nPrefix}.placeholder.onlineInterview.meetingPassword`)"></el-input>
                </template>
              </div>
            </div>
          </div>
        </template>

        <template #hmComment="{ step }">
          <div class="content">
            <div class="content-label">{{ step.contentLabel }}</div>
            <el-input
              v-model="hmComment"
              type="textarea"
              :autosize="{ minRows: 5, maxRows: 8}"
              :placeholder="$t(`${i18nPrefix}.placeholder.commentsPlaceholder`)"
            >
            </el-input>
          </div>
        </template>

        <template #interview="{ step }">
          <div class="interview-container questionnaire-step">
            <div class="content-label">{{ step.contentLabel }}</div>
            <div class="questionnaire-actions">
              <el-button class="questionnaire-action-btn" @click="handleOpenQuestionnaireSelector">{{ $t(`${i18nPrefix}.label.selectInterviewQuestionnaire`) }}</el-button>
              <el-button class="questionnaire-action-btn" @click="handleAddQuestionnaire">{{ $t(`${i18nPrefix}.label.addQuestionnaire`) }}</el-button>
            </div>
            <div v-if="selectedQuestionnaire" class="selected-questionnaire-preview">
              <QuestionnairePreview
                :questionnaire="selectedQuestionnaire"
                :items="selectedQuestionnaire.items || []"
                :showInterviewerAssignment="isGroupInterview"
                :hmOptions="questionInterviewerOptions"
                :questionKeys="questionInterviewerKeys"
                :questionInterviewerMap="questionInterviewerMap"
                :showInterviewerAssignmentErrors="showInterviewerAssignmentErrors"
                @interviewer-assignment-change="handleInterviewerAssignmentChange"
              />
            </div>
          </div>
        </template>
        <template #invitation="{ step }">
          <div class="content-label">{{ step.contentLabel }}</div>
          <div class="microsite-select-container" v-if="hasCRMMicrositeCampaignPermission">
            <span class="microsite-label">{{ $t(`${i18nPrefix}.label.microsite`) }}</span>
            <el-cascader
              class="microsite-select"
              v-model="selectMicrosite"
              popper-class="microsite-select-popper"
              clearable
              :placeholder="$t(`${i18nPrefix}.placeholder.microsite`)"
              :show-all-levels="false"
              :options="micrositeGroupList"
              :props="{ expandTrigger: 'hover', value: 'id' }">
            </el-cascader>
          </div>
          <div class="invitation hm">
            <div class="label">
              <div class="to-select-language">
                <span class="send-type">{{ $t(`${i18nPrefix}.label.sendToHM`) }}</span>
                <span class="language-title">{{ $t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`) }}</span>
                <el-select v-model="sendToHmWithLanguage" class="language-select" :placeholder="$t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`)" :disabled="disabledHmOperateBtn || loadingHmTemplate" @change="handleSendHmLanguageChange">
                  <el-option
                    v-for="(item, index) in languageList"
                    :key="index"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </div>
              <el-switch
                v-model="isSendToHmEmail"
                active-color="#0096cc"
                inactive-color="#ccc">
              </el-switch>
            </div>
            <div class="email-template-content" v-rect-loading="loadingHmTemplate">
              <div class="invitation-subject">
                <div class="subject-label">{{ $t(`${i18nPrefix}.label.invitationSubject`) }}</div>
                <el-input v-model="hmSubjectContent"></el-input>
              </div>
              <TinymceEditor
                v-if="showHmEmailTemplate && activeStepName === step.name"
                v-model="hmTemplateContent"
                class="invitation-content"
                showTemplateField
                :showGenerateAISummaryButton="hasAISummaryPermission"
                :showGenerateAIMatchButton="hasAIResumeMatchPermission"
                :applicationList="applicationList"
                :disabled="false"
                :emailLanguage="sendToHmWithLanguage"
                :aiTemplateField="AI_SUMMARY_TEMPLATE_FIELD"
                :themeColor="emailThemeColor"
                @generateAISummary="handleGenerateAISummary"
                @generateAIMatch="handleGenerateAIMatch"
                @enableOperateBtn="handleEnableOperateBtn"
              >
              </TinymceEditor>
            </div>
          </div>
          <div class="invitation candidate">
            <div class="label">
              <div class="to-select-language">
                <span class="send-type">{{ $t(`${i18nPrefix}.label.sendToCandidate`) }}</span>
                <span class="language-title">{{ $t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`) }}</span>
                <el-select v-model="sendToCandidateWithLanguage" class="language-select" :placeholder="$t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`)" :disabled="loadingCandidateTemplate">
                  <el-option
                    v-for="(item, index) in languageList"
                    :key="index"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </div>
              <el-switch
                v-model="isSendToCandidateEmail"
                active-color="#0096cc"
                inactive-color="#ccc">
              </el-switch>
            </div>
            <div class="email-template-content" v-rect-loading="loadingCandidateTemplate">
              <div class="invitation-subject">
                <div class="subject-label">{{ $t(`${i18nPrefix}.label.invitationSubject`) }}</div>
                <el-input v-model="candidateSubjectContent"></el-input>
              </div>
              <TinymceEditor
                v-if="showCandidateEmailTemplate && activeStepName === step.name"
                class="invitation-content"
                v-model="candidateTemplateContent"
                :showTemplateField="true"
                :disabled="false"
                :emailLanguage="sendToCandidateWithLanguage"
                :themeColor="emailThemeColor"
              >
              </TinymceEditor>
            </div>
          </div>
        </template>
      </StepContent>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <el-button v-show="!hideCancelBtn" class="footer-btn cancel-btn" :disabled="disabledHmOperateBtn" @click="handleCancel">{{ $t('common.btn.prev') }}</el-button>
        <el-button v-show="hideCancelBtn" class="footer-btn save-btn" :disabled="disabledHmOperateBtn || (isSingleHm && selectHiringList.length > 0)" @click="handleInterviewerBtnClick">{{ $t(`${i18nPrefix}.btn.interviewer`) }}</el-button>
        <el-button v-if="activeIndex === stepList.length - 1" :disabled="previewInterviewEmailTypeList.length === 0 || disabledHmOperateBtn" class="footer-btn save-btn" @click="handleOpenPreviewEmailDialog">{{ $t(`${i18nPrefix}.btn.previewEmail`) }}</el-button>
        <el-button class="footer-btn save-btn" :disabled="disabledHmOperateBtn" @click="handleSave">{{ $t(saveBtnLabelI18nKey) }}</el-button>
      </div>
    </template>
    <DialogWrapper
      class="preview-interview-email-dialog"
      appendToBody
      hideFooter
      fullscreen
      :show.sync="showPreviewInterviewEmailDialog"
      :title="$t(`${i18nPrefix}.dialogTitle.previewInterviewEmailDialogTitle`)"
    >
      <div class="header">
        <el-select
          v-model="previewInterviewEmailType"
          class="select-preview-type"
          @change="handleChangePreviewType"
        >
          <el-option
            v-for="(item, index) in previewInterviewEmailTypeList"
            :label="item.label"
            :value="item.value"
            :key="index"
          ></el-option>
        </el-select>
        <div class="operation-btn">
          <el-button class="footer-btn cancel-btn" @click="showPreviewInterviewEmailDialog = false">{{ $t('common.btn.cancel') }}</el-button>
          <el-button class="footer-btn save-btn" @click="handleSave">{{ $t('common.btn.send') }}</el-button>
        </div>
      </div>
      <div v-html="previewInterviewEmailContent" v-rect-loading="loadingInterviewEmail" class="preview-content"></div>
    </DialogWrapper>
    <QuestionnaireSelectorDialog
      ref="questionnaireSelectorDialog"
      :show.sync="showQuestionnaireSelector"
      :selectedQuestionnaire="selectedQuestionnaire"
      @confirm="handleConfirmQuestionnaire"
    />
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import moment from 'moment'
import { isEn, getI18nPropertyValue, addI18nProperty } from '@/i18n'
import { MICROSITE_PATH } from '@/constant/crm'
import HmSelector from '../hmSelector.vue'
import StepContent from '../stepContent.vue'
import Step from '@/components/step/stepMenu.vue'
import DialogWrapper from '../../../../components/dialog/dialogWrapper.vue'
import TinymceEditor from '../../application/components/tinymceEditor.vue'
import { openNewBrowserWindowByUrl, AsyncTask } from '@/utils/common'
import InterviewSchedule from '@/components/interview/interviewSchedule.vue'
import InterviewCalendar from '@/components/interview/interviewCalendar.vue'
import InterviewDuration from '@/components/interview/interviewDuration.vue'
import InterviewTimeRange from '@/components/interview/interviewTimeRange.vue'
import QuestionnairePreview from './components/questionnairePreview.vue'
import QuestionnaireSelectorDialog from './components/questionnaireSelectorDialog.vue'
import {
  DEFAULT_TIME_ZONE,
  INTERVIEW_TIME_TYPE, INTERVIEW_TYPE,
  INTERVIEW_TYPE_F2F,
  INTERVIEW_TYPE_ONLINE,
  INTERVIEW_TYPE_ONLINE_TEAMS_MEETING,
  INTERVIEW_TYPE_ONLINE_ZOOM_MEETING,
  INTERVIEW_TYPE_TELEPHONE
} from '@/constant/interview'

const NAME_LIST = {
  HM_SELECTOR: 'HM_SELECTOR',
  HM_COMMENT: 'HM_COMMENT',
  INTERVIEW_RATING_FORM: 'INTERVIEW_RATING_FORM',
  TIME_AND_LOCATION: 'TIME_AND_LOCATION',
  EMAIL_EDITOR: 'EMAIL_EDITOR'
}

const HM_ON_SITE_TEMPLATE_TYPE = 'Onsite Interview Invitation Email (HM)'
const CANDIDATE_ON_SITE_TEMPLATE_TYPE = 'Onsite Interview Invitation Email (Candidate)'
const HM_ONLINE_TEMPLATE_TYPE = 'Online Interview Invitation Email (HM)'
const CANDIDATE_ONLINE_TEMPLATE_TYPE = 'Online Interview Invitation Email (Candidate)'
const HM_TELEPHONE_TEMPLATE_TYPE = 'Telephone Interview Invitation Email (HM)'
const CANDIDATE_TELEPHONE_TEMPLATE_TYPE = 'Telephone Interview Invitation Email (Candidate)'

// 在邮件模板中使用的占位符，AI summary 将在这个位置插入
const AI_SUMMARY_TEMPLATE_FIELD = '{{ Candidate Info Table }}'

function getQuestionKey (item, index) {
  if (!_.isNil(item.id)) {
    return `item:${item.id}`
  }
  if (!_.isNil(item.questionId)) {
    return `question:${item.questionId}`
  }
  if (!_.isNil(_.get(item, 'question.id'))) {
    return `question:${item.question.id}`
  }
  return `index:${index}`
}

function getHmIdentityKey (hm) {
  if (!_.isNil(hm.id) && hm.id !== '') {
    return `id:${hm.id}`
  }
  const email = _.toLower(_.trim(hm.email || hm.email_address))
  return email ? `email:${email}` : null
}

function getHmLabel (hm) {
  const name = _.trim(`${hm.first_name || ''} ${hm.last_name || ''}`)
  return name || hm.email || hm.email_address
}

let vid = 0

export default {
  components: {
    InterviewTimeRange,
    InterviewDuration,
    InterviewCalendar,
    InterviewSchedule,
    Step,
    DialogWrapper,
    HmSelector,
    StepContent,
    TinymceEditor,
    QuestionnairePreview,
    QuestionnaireSelectorDialog
  },
  props: {
    applicationList: {
      required: true
    },
    isAllApplication: {
      required: false
    },
    searchParams: {
      required: false
    },
    saveWithEvent: {
      type: Boolean,
      default: false
    },
    isEditingGroupInterview: {
      type: Boolean,
      default: false
    }
  },
  metaInfo () {
    return {
      style: [
        { innerHTML: '.el-cascader__dropdown.el-popper.microsite-select-popper .el-cascader-menu{max-width: 300px;}' }
      ]
    }
  },
  data () {
    return {
      AI_SUMMARY_TEMPLATE_FIELD,
      INTERVIEW_TYPE_F2F,
      INTERVIEW_TYPE_ONLINE,
      INTERVIEW_TYPE_ONLINE_TEAMS_MEETING,
      INTERVIEW_TYPE_ONLINE_ZOOM_MEETING,
      INTERVIEW_TYPE_TELEPHONE,
      i18nPrefix: 'admin.application.applicantList',
      show: false,
      loading: false,
      stepLoading: false,
      datePickerOptions: {
        disabledDate (time) {
          return time.getTime() < new Date().getTime() - 86400000
        }
      },
      activeIndex: 0,
      hmComment: null,
      selectedQuestionnaire: null,
      questionInterviewerMap: {},
      showInterviewerAssignmentErrors: false,
      showQuestionnaireSelector: false,
      isSendToHmEmail: true,
      isSendToCandidateEmail: true,
      selectHiringList: [],
      oldSelectHiringList: [],
      timeZoneList: [],
      timeZone: DEFAULT_TIME_ZONE,
      interviewType: INTERVIEW_TYPE_F2F,
      onlineInfo: {
        url: '',
        meetingId: '',
        meetingPassword: ''
      },
      location: null,
      locationList: [],
      startDate: null,
      startTime: null,
      endTime: null,
      locationError: null,
      urlError: null,
      NAME_LIST,
      selectMicrosite: null,
      micrositeList: [],
      sendToHmWithLanguage: 'zh-cn',
      sendToCandidateWithLanguage: 'zh-cn',
      hmSubjectCnContent: null,
      hmSubjectEnContent: null,
      hmTemplateCnContent: null,
      hmTemplateEnContent: null,
      candidateSubjectCnContent: null,
      candidateSubjectEnContent: null,
      candidateTemplateCnContent: null,
      candidateTemplateEnContent: null,
      emailThemeColor: '#0096cc',
      loadingHmTemplate: false,
      loadingCandidateTemplate: false,
      previewInterviewEmailType: 0,
      showPreviewInterviewEmailDialog: false,
      loadingInterviewEmail: false,
      previewInterviewEmailContent: null,
      teamsMeetingUrl: null,
      teamsMeetingId: null,
      teamsMeetingPassword: null,
      zoomMeetingUrl: null,
      zoomMeetingId: null,
      zoomMeetingPassword: null,
      oldTeamsMeetingInfo: {
        meetingUrl: null,
        meetingId: null,
        meetingPassword: null,
        startDate: null,
        startTime: null,
        endTime: null,
        timezone: DEFAULT_TIME_ZONE
      },
      hasMicrosoftGraphAuthToken: false,
      microsoftGraphAuthUrl: null,
      microsoftGraphEventInfo: {
        oldMicrosoftGraphEventId: null,
        newMicrosoftGraphEventId: null
      },
      needSaveMicrosoftOnlineMeeting: false,
      teamsIcon: require('@/assets/iconfont/icon-teams.svg'),
      showTeamsMeetingUrl: false,
      hasLoadScheduleDateDurationList: [],
      hasLoadMeetingRoomScheduleDateDurationList: [],
      hmScheduleList: [],
      meetingRoomScheduleList: [],
      isEditInterview: false,
      editInterviewLoadDataCount: 0,
      editInterviewMeetingRoomLoadDataCount: 0,
      interviewUUid: null,
      hmSendEmailRecordInfo: null,
      candidateSendEmailRecordInfo: null,
      oldInterviewType: null,
      showHmEmailTemplate: false,
      showCandidateEmailTemplate: false,
      emailTemplateLoadVersion: 0,
      disabledHmOperateBtn: false,
      hasLoadHmScheduleMap: {},
      // new
      duration: {
        hour: 1,
        minute: 0
      },
      interviewTime: null,
      interviewEndTime: null,
      selectedDate: null,
      selectedDateTimeList: [],
      isSingleHm: true,
      isAutomate: true,
      isGroupInterview: false,
      interviewMeetingRoomList: [],
      interviewMeetingRoom: null,
      telephone: null,
      interviewTimeError: null,
      interviewMeetingRoomError: null,
      telephoneError: null,
      hasCleanHmScheduleEmailList: [],
      showInterviewSchedule: false,
      groupInterviewName: null,
      groupInterviewNameError: false
    }
  },
  computed: {
    locationId () {
      const location = _.find(this.locationList, this.location)
      if (location) {
        return location.id
      }
      return null
    },
    previewInterviewEmailTypeList () {
      const list = []
      if (this.isSendToHmEmail) {
        list.push({
          label: this.$t(`${this.i18nPrefix}.label.previewHmEmail`),
          value: 0
        })
      }

      if (this.isSendToCandidateEmail) {
        list.push({
          label: this.$t(`${this.i18nPrefix}.label.previewCandidateEmail`),
          value: 1
        })
      }
      return list
    },
    languageList () {
      const list = [
        {
          label: this.$t('common.label.english'),
          value: 'en'
        },
        {
          label: this.$t('common.label.chinese'),
          value: 'zh-cn'
        }
      ]
      return list
    },
    hmSubjectContent: {
      get () {
        if (this.hasSendHmEmailInfo) {
          return this.hasSendHmEmailInfo.subject
        }
        return this.sendToHmWithLanguage === 'en' ? this.hmSubjectEnContent : this.hmSubjectCnContent
      },
      set (value) {
        if (this.hasSendHmEmailInfo) {
          this.hasSendHmEmailInfo.subject = value
        }
        if (this.sendToHmWithLanguage === 'en') {
          this.hmSubjectEnContent = value
        } else {
          this.hmSubjectCnContent = value
        }
      }
    },
    hmTemplateContent: {
      get () {
        if (this.hasSendHmEmailInfo) {
          return this.hasSendHmEmailInfo.content
        }
        return this.sendToHmWithLanguage === 'en' ? this.hmTemplateEnContent : this.hmTemplateCnContent
      },
      set (value) {
        if (this.hasSendHmEmailInfo) {
          this.hasSendHmEmailInfo.content = value
        }
        if (this.sendToHmWithLanguage === 'en') {
          this.hmTemplateEnContent = value
        } else {
          this.hmTemplateCnContent = value
        }
      }
    },
    candidateSubjectContent: {
      get () {
        if (this.hasSendCandidateEmailInfo) {
          return this.hasSendCandidateEmailInfo.subject
        }
        return this.sendToCandidateWithLanguage === 'en' ? this.candidateSubjectEnContent : this.candidateSubjectCnContent
      },
      set (value) {
        if (this.hasSendCandidateEmailInfo) {
          this.hasSendCandidateEmailInfo.subject = value
        }
        if (this.sendToCandidateWithLanguage === 'en') {
          this.candidateSubjectEnContent = value
        } else {
          this.candidateSubjectCnContent = value
        }
      }
    },
    candidateTemplateContent: {
      get () {
        if (this.hasSendCandidateEmailInfo) {
          return this.hasSendCandidateEmailInfo.content
        }
        return this.sendToCandidateWithLanguage === 'en' ? this.candidateTemplateEnContent : this.candidateTemplateCnContent
      },
      set (value) {
        if (this.hasSendCandidateEmailInfo) {
          this.hasSendCandidateEmailInfo.content = value
        }
        if (this.sendToCandidateWithLanguage === 'en') {
          this.candidateTemplateEnContent = value
        } else {
          this.candidateTemplateCnContent = value
        }
      }
    },
    hasSendHmEmailInfo () {
      if (!this.hmSendEmailRecordInfo) {
        return
      }
      const info = _.get(this.hmSendEmailRecordInfo, this.sendToHmWithLanguage)
      return info
    },
    hasSendCandidateEmailInfo () {
      if (!this.candidateSendEmailRecordInfo) {
        return
      }
      const info = _.get(this.candidateSendEmailRecordInfo, this.sendToCandidateWithLanguage)
      return info
    },
    stepList () {
      const step = [
        {
          name: NAME_LIST.HM_SELECTOR,
          title: this.$t(`${this.i18nPrefix}.title.interviewerAndTimeAndLocation`),
          contentLabel: this.$t(`${this.i18nPrefix}.label.interviewerAndTimeAndLocation`),
          contentSlot: 'hmSelector'
        },
        {
          hidden: this.applicationList.length > 1,
          name: NAME_LIST.HM_COMMENT,
          title: this.$t(`${this.i18nPrefix}.title.addComments`),
          contentLabel: this.$t(`${this.i18nPrefix}.label.hiringComments`),
          contentSlot: 'hmComment'
        },
        {
          name: NAME_LIST.INTERVIEW_RATING_FORM,
          title: this.$t(`${this.i18nPrefix}.title.selectInterviewQuestionnaire`),
          contentLabel: this.$t(`${this.i18nPrefix}.label.interviewQuestionnaire`),
          contentSlot: 'interview'
        },
        {
          name: NAME_LIST.EMAIL_EDITOR,
          title: this.$t(`${this.i18nPrefix}.title.sendInvitation`),
          contentLabel: this.$t(`${this.i18nPrefix}.label.invitation`),
          contentSlot: 'invitation'
        }
      ]
      return _.filter(step, item => !item.hidden)
    },
    activeStepName () {
      return this.stepList[this.activeIndex].name
    },
    saveBtnLabelI18nKey () {
      return `common.btn.${this.activeIndex === this.stepList.length - 1 ? 'send' : 'next'}`
    },
    hideCancelBtn () {
      return this.activeIndex === 0
    },
    hasCRMMicrositeCampaignPermission () {
      return this.hasAnyPermission('PREMIUM_MICROSITE_CAMPAIGN_MANAGEMENT')
    },
    interviewTypeList () {
      const interviewTypeList = [
        {
          key: INTERVIEW_TYPE_F2F,
          label: this.$t(`${this.i18nPrefix}.label.interviewType.f2fType`)
        },
        {
          key: INTERVIEW_TYPE_TELEPHONE,
          label: this.$t(`${this.i18nPrefix}.label.interviewType.telephone`)
        },
        {
          hidden: !this.hasQuicklyCreateMeetingPremium,
          key: INTERVIEW_TYPE_ONLINE_TEAMS_MEETING,
          label: this.$t(`${this.i18nPrefix}.label.interviewType.teams`),
          handle: () => {
            this.checkCurrentAccountAuthGraph()
          }
        },
        {
          key: INTERVIEW_TYPE_ONLINE_ZOOM_MEETING,
          label: this.$t(`${this.i18nPrefix}.label.interviewType.zoom`)
        },
        {
          key: INTERVIEW_TYPE_ONLINE,
          label: this.$t(`${this.i18nPrefix}.label.interviewType.online`)
        }
      ]
      return _.filter(interviewTypeList, item => !item.hidden)
    },
    micrositeGroupList () {
      const micrositeList = this.micrositeList
      const groupList = []
      _.forEach(_.groupBy(micrositeList, 'group.id'), (value, key) => {
        if (key !== 'undefined') {
          const group = value[0].group
          groupList.push({
            label: group.i18nName,
            id: group.id,
            children: value
          })
        } else {
          groupList.push({
            label: '未分组',
            id: 0,
            children: value
          })
        }
      })
      return groupList
    },
    hasQuicklyCreateMeetingPremium () {
      return this.hasAnyPermission('PREMIUM_QUICKLY_CREATE_TEAMS_MEETING')
    },
    hasViewHiringManagerAvailabilityPremium () {
      return this.hasAnyPermission('PREMIUM_VIEW_HIRING_MANAGER_AVAILABILITY')
    },
    hasAutomatedSchedulerPremium () {
      return this.hasAnyPermission('PREMIUM_AUTOMATED_SCHEDULER')
    },
    hasAISummaryPermission () {
      return this.hasAnyPermission('PREMIUM_AI_SUMMARY')
    },
    hasAIResumeMatchPermission () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    durationMinutes () {
      return this.duration.hour * 60 + this.duration.minute
    },
    selectedDateList () {
      return this.selectedDateTimeList.map(dateTime => {
        return moment(dateTime.dateTime).format('YYYY-MM-DD')
      })
    },
    maxSelectScheduleCount () {
      if (this.isGroupInterview || this.applicationList.length > 1 || !this.hasAutomatedSchedulerPremium) {
        return 1
      }
      return 3
    },
    hasMultipleCandidates () {
      return this.applicationList.length > 1
    },
    showInterviewModel () {
      return (this.applicationList.length === 1 && this.hasAutomatedSchedulerPremium) || this.hasMultipleCandidates || this.isEditingGroupInterview
    },
    showGroupInterviewOption () {
      return this.hasMultipleCandidates || this.isEditingGroupInterview
    },
    canUseAutomatedScheduler () {
      return this.hasAutomatedSchedulerPremium && !this.isAllApplication && this.applicationList.length === 1 && this.isSingleHm
    },
    isAutomatedSchedulerActive () {
      return this.canUseAutomatedScheduler && this.isAutomate
    },
    showAutomatedSchedulerOption () {
      return this.canUseAutomatedScheduler
    },
    questionInterviewerKeys () {
      return _.map(_.get(this.selectedQuestionnaire, 'items', []), (item, index) => getQuestionKey(item, index))
    },
    questionInterviewerOptions () {
      return _.chain(this.selectHiringList)
        .map(hm => ({
          key: getHmIdentityKey(hm),
          label: getHmLabel(hm),
          id: !_.isNil(hm.id) && hm.id !== '' ? hm.id : null,
          email_address: _.trim(hm.email || hm.email_address)
        }))
        .filter('key')
        .uniqBy('key')
        .value()
    },
    questionHmAssignments () {
      const hmOptionMap = _.keyBy(this.questionInterviewerOptions, 'key')
      return _.map(_.get(this.selectedQuestionnaire, 'items', []), (item, index) => {
        const hmKeys = this.questionInterviewerMap[this.questionInterviewerKeys[index]] || []
        return {
          questionnaire_item_id: item.id,
          hire_managers: _.chain(hmKeys)
            .map(hmKey => hmOptionMap[hmKey])
            .compact()
            .map(hm => _.pick(hm, ['id', 'email_address']))
            .value()
        }
      })
    }
  },
  created () {
    this.selectedDate = moment().format('YYYY-MM-DD')
  },
  methods: {
    ...mapActions('application', ['getApplicationAISummary', 'getApplicationAIMatchInfo']),
    ...mapActions('common', ['getTimeZoneList']),
    ...mapActions('jobManagement', ['getJobLocationList']),
    ...mapActions('crm', ['searchMicrosite']),
    ...mapActions('notificationTemplate', ['getCompanyInterviewEmailTemplate']),
    ...mapActions('interview', ['previewInterviewEmailInfo', 'getInterviewInvitationRecordInfo', 'getHmInterviewScheduleList', 'sendInterviewInvitation']),
    ...mapActions('microsoftGraph', ['checkMicrosoftGraphAuth', 'createOnlineMeetingEvent', 'updateOnlineMeetingEvent', 'deleteOnlineMeetingEvent']),
    ...mapActions('ai', ['createAISummary', 'getAISummaryInfoByTaskId', 'createAIResumeMatch', 'getAIMatchInfoByTaskId']),
    ...mapActions('interviewMeetingRoom', ['getInterviewMeetingRoomList', 'getInterviewMeetingRoomSchedule']),
    handleOpenQuestionnaireSelector () {
      this.showQuestionnaireSelector = true
    },
    handleAddQuestionnaire () {
      const route = this.$router.resolve({ path: '/v-admin/questionnaire-management/questionnaire/add' })
      window.open(route.href, '_blank')
    },
    handleConfirmQuestionnaire (questionnaire) {
      const currentQuestionnaireId = _.get(this.selectedQuestionnaire, 'id')
      const nextQuestionnaireId = _.get(questionnaire, 'id')
      this.selectedQuestionnaire = questionnaire
      if (currentQuestionnaireId !== nextQuestionnaireId) {
        this.resetQuestionInterviewerAssignments()
      }
      this.fillSingleHmQuestionInterviewerAssignments()
    },
    handleInterviewerAssignmentChange ({ questionKey, hmKeys }) {
      this.$set(this.questionInterviewerMap, questionKey, hmKeys)
    },
    resetQuestionInterviewerAssignments () {
      this.questionInterviewerMap = {}
      this.showInterviewerAssignmentErrors = false
    },
    fillSingleHmQuestionInterviewerAssignments () {
      if (!this.isGroupInterview || !this.selectedQuestionnaire || this.questionInterviewerOptions.length !== 1) {
        return
      }
      const hmKey = this.questionInterviewerOptions[0].key
      this.questionInterviewerMap = _.reduce(this.questionInterviewerKeys, (result, questionKey) => {
        result[questionKey] = [hmKey]
        return result
      }, {})
      this.showInterviewerAssignmentErrors = false
    },
    restoreQuestionInterviewerAssignments (assignments) {
      const hmOptionMap = _.keyBy(this.questionInterviewerOptions, 'key')
      const itemAssignmentMap = _.keyBy(assignments || [], assignment => String(assignment.questionnaire_item_id))
      this.questionInterviewerMap = _.reduce(_.get(this.selectedQuestionnaire, 'items', []), (result, item, index) => {
        const questionKey = getQuestionKey(item, index)
        const assignment = itemAssignmentMap[String(item.id)]
        result[questionKey] = _.chain(_.get(assignment, 'hire_managers', []))
          .map(hm => getHmIdentityKey(hm))
          .filter(hmKey => hmKey && hmOptionMap[hmKey])
          .uniq()
          .value()
        return result
      }, {})
    },
    reconcileQuestionInterviewerAssignments () {
      const availableHmKeys = _.map(this.questionInterviewerOptions, 'key')
      this.questionInterviewerMap = _.mapValues(this.questionInterviewerMap, hmKeys => {
        return _.intersection(hmKeys, availableHmKeys)
      })
    },
    validateQuestionInterviewerAssignments () {
      if (!this.isGroupInterview) {
        this.showInterviewerAssignmentErrors = false
        return true
      }
      if (!this.selectedQuestionnaire) {
        this.showInterviewerAssignmentErrors = false
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.groupInterviewQuestionnaireRequired`))
        return false
      }
      const everyQuestionHasInterviewer = _.every(this.questionInterviewerKeys, questionKey => {
        return !_.isEmpty(this.questionInterviewerMap[questionKey])
      })
      this.showInterviewerAssignmentErrors = !everyQuestionHasInterviewer
      if (!everyQuestionHasInterviewer) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionInterviewerRequired`))
        return false
      }
      const selectedHmKeys = _.map(this.questionInterviewerOptions, 'key')
      const assignedHmKeys = _.chain(this.questionInterviewerKeys)
        .map(questionKey => this.questionInterviewerMap[questionKey] || [])
        .flatten()
        .compact()
        .uniq()
        .value()
      if (!_.isEmpty(_.difference(selectedHmKeys, assignedHmKeys))) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.allHmMustBeAssigned`))
        return false
      }
      return true
    },
    validateGroupInterview () {
      if (!this.isGroupInterview) {
        this.groupInterviewNameError = false
        return true
      }
      const groupInterviewName = _.trim(this.groupInterviewName)
      if (!groupInterviewName) {
        this.groupInterviewNameError = true
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.groupInterviewNameRequired`))
        return false
      }
      this.groupInterviewNameError = false
      this.groupInterviewName = groupInterviewName
      return true
    },
    handleGroupInterviewNameInput (value) {
      if (_.trim(value)) {
        this.groupInterviewNameError = false
      }
    },
    handleSelectSchedule (schedule) {
      const dateTime = {
        vid: vid,
        date: this.selectedDate,
        time: schedule.time,
        dateTime: `${this.selectedDate} ${schedule.time}`,
        duplicate: false
      }
      const index = _.findIndex(this.selectedDateTimeList, { dateTime: dateTime.dateTime })
      if (index > -1) {
        this.handleDeleteTime(index)
        return
      }
      if (this.selectedDateTimeList.length >= this.maxSelectScheduleCount) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.maxSelectScheduleCount`, { maxCount: this.maxSelectScheduleCount }))
        return
      }
      this.selectedDateTimeList.push(dateTime)
      this.selectedDateTimeList = _.sortBy(this.selectedDateTimeList, dateTime => moment(dateTime.dateTime))
      this.checkSelectedDateTimeDuplicate()
      vid = vid + 1
      this.calculateMeetingRoomSchedule()
      this.resizeTeamsMeetingUrl()
    },
    handleDeleteTime (index) {
      this.selectedDateTimeList.splice(index, 1)
      this.checkSelectedDateTimeDuplicate()
      this.calculateMeetingRoomSchedule()
      this.resizeTeamsMeetingUrl()
    },
    handleTimeRangeChange (index, dateTime) {
      this.selectedDateTimeList.splice(index, 1, dateTime)
      this.checkSelectedDateTimeDuplicate()
      this.calculateMeetingRoomSchedule()
      this.resizeTeamsMeetingUrl()
    },
    handleEndTimeClick () {
      this.$refs.interviewDuration?.scrollToCenterAndHighlight()
    },
    checkSelectedDateTimeDuplicate () {
      let valid = true
      this.selectedDateTimeList = _.map(this.selectedDateTimeList, dateTime => {
        dateTime.duplicate = false
        return dateTime
      })
      const grouped = _.groupBy(_.range(this.selectedDateTimeList.length), (index) => this.selectedDateTimeList[index].dateTime)
      const duplicates = _.pickBy(grouped, (indexes) => indexes.length > 1)
      if (!_.isEmpty(duplicates)) {
        _.forEach(duplicates, indexes => {
          _.forEach(indexes, index => {
            this.selectedDateTimeList[index].duplicate = true
          })
        })
        valid = false
      }
      return valid
    },
    async loadApplicationAISummary () {
      const params = {
        job_id: this.applicationList[0].job.id,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id).join(',')
      }
      const resp = await this.getApplicationAISummary(params)
      if (resp) {
        _.forEach(resp, (aiSummaryInfo, candidateId) => {
          const application = _.find(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          const index = _.findIndex(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          application.candidate.ai_summary_info = aiSummaryInfo
          this.applicationList.splice(index, 1, application)
        })
      }
    },
    async loadApplicationAIMatchInfo () {
      const params = {
        job_id: this.applicationList[0].job.id,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id).join(',')
      }
      const resp = await this.getApplicationAIMatchInfo(params)
      if (resp) {
        _.forEach(resp, (aiMatchInfo, candidateId) => {
          const application = _.find(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          const index = _.findIndex(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          application.candidate.ai_match_info = aiMatchInfo
          this.applicationList.splice(index, 1, application)
        })
      }
    },
    async loadHmScheduleList (startDate, loadEndDateData, needCheckDate, hmList) {
      if (!this.hasViewHiringManagerAvailabilityPremium || (_.isEmpty(this.selectHiringList) && !hmList)) {
        return
      }
      let loadMore = true
      const tempStartDate = moment(startDate).format('YYYY-MM-DD HH:mm:ss')
      _.forEach(this.hasLoadScheduleDateDurationList, item => {
        const start = item.start
        const end = item.end
        if (moment(tempStartDate).isSameOrAfter(moment(start)) && moment(tempStartDate).isSameOrBefore(moment(end))) {
          loadMore = false
        }
      })
      if (!loadMore) {
        return
      }

      let startTime
      let endTime
      if (this.isEditInterview) {
        this.editInterviewLoadDataCount += 1
      }
      if (loadEndDateData || (this.isEditInterview && this.editInterviewLoadDataCount === 1)) {
        startTime = moment().startOf('day')
        endTime = startDate ? moment(startDate).add(3, 'days').endOf('day') : moment(startTime).clone().add(3, 'days').endOf('day')
      } else {
        const start = startDate ? moment(startDate) : moment()
        startTime = moment(`${start.format('YYYY-MM-DD')} 00:00:00`, 'YYYY-MM-DD HH:mm:ss')
        endTime = moment(startTime).clone().add(3, 'days').endOf('day')
      }
      if (needCheckDate) {
        const durationDay = endTime.diff(startTime, 'days')
        if (durationDay > 62) {
          this.$message({
            type: 'warning',
            message: this.$t(`${this.i18nPrefix}.message.scheduleSearchDayOutLimit`)
          })
        }
      }
      if (!hmList) {
        hmList = this.selectHiringList
      }
      const params = {
        hm_info_list: hmList,
        start_date_time: startTime.format('YYYY-MM-DD HH:mm:ss'),
        end_date_time: endTime.format('YYYY-MM-DD HH:mm:ss'),
        availability_view_interval: 60,
        timezone: this.timeZone
      }
      this.stepLoading = true
      try {
        const resp = await this.getHmInterviewScheduleList(params)
        if (resp) {
          this.hasLoadScheduleDateDurationList.push({
            start: startTime.format('YYYY-MM-DD HH:mm:ss'),
            end: endTime.format('YYYY-MM-DD HH:mm:ss')
          })
          _.forEach(resp, (item) => {
            const email = item.email
            if (this.isEditInterview && this.interviewTime && _.find(this.selectHiringList, { email }) && !_.includes(this.hasCleanHmScheduleEmailList, email)) {
              item.event_list = _.filter(item.event_list, event => {
                if (!_.includes(this.hasCleanHmScheduleEmailList, email) && moment(event.start_time).isSame(this.interviewTime) && moment(event.end_time).isSame(this.interviewEndTime)) {
                  this.hasCleanHmScheduleEmailList.push(email)
                  return false
                }
                return true
              })
            }
            const scheduleIndex = _.findIndex(this.hmScheduleList, { email })
            if (scheduleIndex !== -1) {
              item.event_list = _.concat(this.hmScheduleList[scheduleIndex].event_list, item.event_list)
              this.hmScheduleList.splice(scheduleIndex, 1, item)
            } else {
              this.hmScheduleList.push(item)
            }
          })
        }
      } finally {
        this.stepLoading = false
      }
    },
    handleOpenPreviewEmailDialog () {
      this.showPreviewInterviewEmailDialog = true
      this.handleChangePreviewType()
    },
    async handleChangePreviewType () {
      this.previewInterviewEmailContent = null
      const duration = this.durationMinutes
      let startTime = ''
      if (this.selectedDateTimeList.length === 1) {
        startTime = moment(this.selectedDateTimeList[0].dateTime).format('YYYY-MM-DD HH:mm:ss')
      }
      const templateContent = this.previewInterviewEmailType ? this.candidateTemplateContent : this.hmTemplateContent
      const languageCode = this.previewInterviewEmailType ? this.sendToCandidateWithLanguage : this.sendToHmWithLanguage
      if (!templateContent) {
        return
      }
      const isTeamsMeeting = this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING
      const isZoomMeeting = this.interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING
      let meetingUrl = this.onlineInfo.url
      let meetingID = this.onlineInfo.meetingId
      let meetingPassword = this.onlineInfo.meetingPassword
      if (isTeamsMeeting) {
        meetingUrl = this.teamsMeetingUrl
        meetingID = this.teamsMeetingId
        meetingPassword = this.teamsMeetingPassword
      } else if (isZoomMeeting) {
        meetingUrl = this.zoomMeetingUrl
        meetingID = this.zoomMeetingId
        meetingPassword = this.zoomMeetingPassword
      }
      const params = {
        is_candidate: this.previewInterviewEmailType,
        template_content: templateContent,
        interview_start_time: startTime,
        duration: duration,
        job_id: this.applicationList[0].job.id,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id),
        hm_info_list: this.selectHiringList,
        interview_address: this.location?.value || this.location,
        meeting_room_id: this.interviewMeetingRoom,
        timezone: this.timeZone,
        interview_type: isTeamsMeeting || isZoomMeeting ? INTERVIEW_TYPE_ONLINE : this.interviewType,
        is_online: this.interviewType === INTERVIEW_TYPE_ONLINE || this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING || this.interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING,
        online_meeting_info: {
          provider: isZoomMeeting ? 'zoom' : (isTeamsMeeting ? 'teams' : undefined),
          url: meetingUrl,
          address: this.location?.value || this.location,
          meeting_id: meetingID,
          meeting_password: meetingPassword
        },
        microsite_id: this.selectMicrosite ? this.selectMicrosite[1] : '',
        language_code: languageCode,
        theme_color: this.emailThemeColor,
        telephone: this.telephone
      }
      this.loadingInterviewEmail = true
      try {
        const resp = await this.previewInterviewEmailInfo(params)
        if (resp) {
          this.previewInterviewEmailContent = resp
        }
      } finally {
        this.loadingInterviewEmail = false
      }
    },
    async loadHmEmailTemplate () {
      const isOnline = this.interviewType === INTERVIEW_TYPE_ONLINE || this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING || this.interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING
      let hmTemplateType = null
      if (isOnline) {
        hmTemplateType = HM_ONLINE_TEMPLATE_TYPE
      } else if (this.interviewType === INTERVIEW_TYPE_TELEPHONE) {
        hmTemplateType = HM_TELEPHONE_TEMPLATE_TYPE
      } else {
        hmTemplateType = HM_ON_SITE_TEMPLATE_TYPE
      }
      return this.getCompanyInterviewEmailTemplate({
        email_template_type: hmTemplateType,
        job_id: this.applicationList[0].job.id
      })
    },
    async loadCandidateEmailTemplate () {
      const isOnline = this.interviewType === INTERVIEW_TYPE_ONLINE || this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING || this.interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING
      let candidateTemplateType = null
      if (isOnline) {
        candidateTemplateType = CANDIDATE_ONLINE_TEMPLATE_TYPE
      } else if (this.interviewType === INTERVIEW_TYPE_TELEPHONE) {
        candidateTemplateType = CANDIDATE_TELEPHONE_TEMPLATE_TYPE
      } else {
        candidateTemplateType = CANDIDATE_ON_SITE_TEMPLATE_TYPE
      }
      return this.getCompanyInterviewEmailTemplate({
        email_template_type: candidateTemplateType,
        job_id: this.applicationList[0].job.id
      })
    },
    async loadHasSendEmailContent () {
      const interviewType = this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING || this.interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING ? INTERVIEW_TYPE_ONLINE : this.interviewType
      if (!this.interviewUUid) {
        return null
      }
      if (this.oldInterviewType !== interviewType) {
        return null
      }
      const params = {
        interview_uuid: this.interviewUUid
      }
      return this.getInterviewInvitationRecordInfo(params)
    },
    invalidateEmailTemplateLoad () {
      this.emailTemplateLoadVersion += 1
      this.showHmEmailTemplate = false
      this.showCandidateEmailTemplate = false
      this.loadingHmTemplate = false
      this.loadingCandidateTemplate = false
    },
    async prepareEmailTemplates () {
      const loadVersion = this.emailTemplateLoadVersion + 1
      this.emailTemplateLoadVersion = loadVersion
      this.showHmEmailTemplate = false
      this.showCandidateEmailTemplate = false
      this.loadingHmTemplate = true
      this.loadingCandidateTemplate = true
      this.emailThemeColor = '#0096cc'

      const promiseList = [
        this.loadHmEmailTemplate(),
        this.loadCandidateEmailTemplate(),
        this.isEditInterview ? this.loadHasSendEmailContent() : Promise.resolve(null)
      ]
      if (this.hasAISummaryPermission) {
        promiseList.push(this.loadApplicationAISummary())
      }
      if (this.hasAIResumeMatchPermission) {
        promiseList.push(this.loadApplicationAIMatchInfo())
      }

      let hmTemplateResp = null
      let candidateTemplateResp = null
      try {
        const respList = await Promise.all(promiseList)
        if (loadVersion !== this.emailTemplateLoadVersion) {
          return
        }
        hmTemplateResp = respList[0]
        candidateTemplateResp = respList[1]
        const emailRecordResp = respList[2]

        if (hmTemplateResp) {
          this.hmSubjectCnContent = hmTemplateResp.subject_cn
          this.hmSubjectEnContent = hmTemplateResp.subject_en
          this.hmTemplateCnContent = hmTemplateResp.content_cn
          this.hmTemplateEnContent = hmTemplateResp.content_en
          this.emailThemeColor = hmTemplateResp.theme_color || '#0096cc'
        }
        if (candidateTemplateResp) {
          this.candidateSubjectCnContent = candidateTemplateResp.subject_cn
          this.candidateSubjectEnContent = candidateTemplateResp.subject_en
          this.candidateTemplateCnContent = candidateTemplateResp.content_cn
          this.candidateTemplateEnContent = candidateTemplateResp.content_en
          this.emailThemeColor = candidateTemplateResp.theme_color || this.emailThemeColor
        }
        this.hmSendEmailRecordInfo = emailRecordResp ? emailRecordResp.HM : null
        this.candidateSendEmailRecordInfo = emailRecordResp ? emailRecordResp.CANDIDATE : null
      } finally {
        if (loadVersion === this.emailTemplateLoadVersion) {
          this.loadingHmTemplate = false
          this.loadingCandidateTemplate = false
        }
      }

      if (loadVersion !== this.emailTemplateLoadVersion) {
        return
      }
      this.showHmEmailTemplate = Boolean(hmTemplateResp)
      this.showCandidateEmailTemplate = Boolean(candidateTemplateResp)
      await this.$nextTick()
      if (loadVersion === this.emailTemplateLoadVersion) {
        this.resetTinymceDialogStyle()
      }
    },
    async showDialog (defaultInvitationIfo) {
      this.invalidateEmailTemplateLoad()
      this.show = true
      this.stepLoading = true
      await this.$nextTick()
      this.activeIndex = 0
      this.interviewUUid = ''
      this.hmSendEmailRecordInfo = null
      this.candidateSendEmailRecordInfo = null
      this.selectHiringList = []
      this.oldSelectHiringList = []
      this.hmComment = null
      this.selectedQuestionnaire = null
      this.resetQuestionInterviewerAssignments()
      this.isGroupInterview = false
      this.groupInterviewName = null
      this.groupInterviewNameError = false
      this.selectedDateTimeList = []
      this.hasCleanHmScheduleEmailList = []
      this.startDate = this.hasViewHiringManagerAvailabilityPremium ? new Date() : null
      const currentTime = moment()
      const minutes = currentTime.minutes()
      const targetMinutes = _.ceil(minutes / 30) * 30
      const targetHour = targetMinutes === 60 ? currentTime.hours() + 1 : currentTime.hours()
      this.startTime = this.hasViewHiringManagerAvailabilityPremium ? moment(`${targetHour}:${targetMinutes === 60 ? '00' : targetMinutes}`, 'H:mm').format('H:mm:ss') : null
      if (this.hasViewHiringManagerAvailabilityPremium) {
        if (targetHour === 23) {
          this.endTime = moment(`23:${targetMinutes + _.floor((59 - targetMinutes) / 30) * 30}:00`, 'H:mm:ss').format('H:mm:ss')
        } else {
          this.endTime = moment(this.startTime, 'H:mm:ss').add(1, 'hours').format('H:mm:ss')
        }
      } else {
        this.endTime = null
      }
      this.interviewType = INTERVIEW_TYPE_F2F
      this.telephone = null
      this.location = null
      this.selectMicrosite = null
      this.onlineInfo.url = null
      this.onlineInfo.meetingId = null
      this.onlineInfo.meetingPassword = null
      this.hasMicrosoftGraphAuthToken = false
      this.microsoftGraphAuthUrl = null
      this.microsoftGraphEventInfo = {
        oldMicrosoftGraphEventId: null,
        newMicrosoftGraphEventId: null
      }
      this.oldTeamsMeetingInfo = {
        meetingUrl: null,
        meetingId: null,
        meetingPassword: null,
        startDate: null,
        startTime: null,
        endTime: null,
        timezone: DEFAULT_TIME_ZONE
      }
      this.teamsMeetingUrl = null
      this.teamsMeetingId = null
      this.teamsMeetingPassword = null
      this.zoomMeetingUrl = null
      this.zoomMeetingId = null
      this.zoomMeetingPassword = null
      this.needSaveMicrosoftOnlineMeeting = false
      this.showTeamsMeetingUrl = false
      this.hasLoadScheduleDateDurationList = []
      this.hasLoadMeetingRoomScheduleDateDurationList = []
      this.hasLoadHmScheduleMap = {}
      this.hmScheduleList = []
      this.isEditInterview = false
      this.editInterviewLoadDataCount = 0
      this.editInterviewMeetingRoomLoadDataCount = 0

      this.isSingleHm = this.applicationList.length === 1
      this.isAutomate = this.canUseAutomatedScheduler
      this.interviewMeetingRoom = null

      const companyInfo = this.applicationList[0].job.company

      const isHsbcCompany = companyInfo.root_id === 6429 || companyInfo.root_company_id === 6429
      if (isHsbcCompany) {
        this.isSendToHmEmail = false
      }

      if (defaultInvitationIfo) {
        this.isEditInterview = true
        this.interviewUUid = defaultInvitationIfo.uuid
        this.isGroupInterview = defaultInvitationIfo.is_group_interview
        this.groupInterviewName = defaultInvitationIfo.group_name
        this.selectHiringList = _.map(defaultInvitationIfo.hire_manager, hm => {
          return {
            ...hm,
            email: hm.email_address,
            company: hm.company_id
          }
        })
        this.oldSelectHiringList = this.selectHiringList
        this.hmComment = defaultInvitationIfo.hr_comments
        if (defaultInvitationIfo.questionnaire_id) {
          this.selectedQuestionnaire = await this.$refs.questionnaireSelectorDialog.loadQuestionnaireById(defaultInvitationIfo.questionnaire_id, {
            name_en: defaultInvitationIfo.questionnaire_name
          })
          this.restoreQuestionInterviewerAssignments(defaultInvitationIfo.question_hm_assignments)
        }
        if (defaultInvitationIfo.interview_time) {
          this.interviewTime = moment(defaultInvitationIfo.interview_time)
          this.interviewEndTime = moment(defaultInvitationIfo.interview_end_time)
        }
        this.isSingleHm = this.selectHiringList.length === 1
        this.isAutomate = defaultInvitationIfo.interview_time_type === INTERVIEW_TIME_TYPE.AUTOMATIC
        if (!this.isAutomate) {
          const availableInterviewTimeList = defaultInvitationIfo.available_interview_time_list
          this.selectedDateTimeList = _.map(availableInterviewTimeList, (time, index) => {
            const startDate = moment(time.startDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
            const startTime = moment(time.startTime, 'HH:mm:ss').format('HH:mm')
            return {
              vid: `old-${this.interviewUUid}-${index}`,
              date: startDate,
              time: startTime,
              dateTime: `${startDate} ${startTime}`,
              duplicate: false
            }
          })
        }
        if (defaultInvitationIfo.interview_time) {
          const startTime = moment(defaultInvitationIfo.interview_time)
          this.selectedDateTimeList = [{
            vid: `old-${this.interviewUUid}-0`,
            date: startTime.clone().format('YYYY-MM-DD'),
            time: startTime.clone().format('HH:mm'),
            dateTime: startTime.clone().format('YYYY-MM-DD HH:mm'),
            duplicate: false
          }]
        }
        this.timeZone = defaultInvitationIfo.timezone
        this.interviewMeetingRoom = defaultInvitationIfo.meeting_room?.id
        this.telephone = defaultInvitationIfo.telephone
        this.location = _.find(this.locationList, { value: defaultInvitationIfo.interview_address }) || defaultInvitationIfo.interview_address
        const sendStatus = _.toInteger(defaultInvitationIfo.send_status) || 1
        if (isHsbcCompany) {
          this.isSendToHmEmail = false
        } else {
          this.isSendToHmEmail = sendStatus === 1 || sendStatus === 2
        }
        this.isSendToCandidateEmail = sendStatus === 1 || sendStatus === 3
        const interviewType = defaultInvitationIfo.interview_type
        this.interviewType = INTERVIEW_TYPE.includes(interviewType) ? interviewType : INTERVIEW_TYPE_F2F
        this.oldInterviewType = this.interviewType
        const onlineInfo = defaultInvitationIfo.online_info || {}
        this.onlineInfo.url = onlineInfo.url
        this.onlineInfo.meetingId = onlineInfo.meeting_id
        this.onlineInfo.meetingPassword = onlineInfo.meeting_password
        const micrositeInfo = defaultInvitationIfo.microsite_info
        if (micrositeInfo) {
          this.selectMicrosite = [micrositeInfo.group_id, micrositeInfo.id]
        }
        if (this.interviewType === INTERVIEW_TYPE_ONLINE && onlineInfo.provider === 'zoom') {
          this.interviewType = INTERVIEW_TYPE_ONLINE_ZOOM_MEETING
          this.zoomMeetingUrl = onlineInfo.url
          this.zoomMeetingId = onlineInfo.meeting_id
          this.zoomMeetingPassword = onlineInfo.meeting_password
          this.onlineInfo.url = null
          this.onlineInfo.meetingId = null
          this.onlineInfo.meetingPassword = null
        } else if (this.interviewType === INTERVIEW_TYPE_ONLINE && onlineInfo.provider === 'teams') {
          this.interviewType = INTERVIEW_TYPE_ONLINE_TEAMS_MEETING
          this.teamsMeetingUrl = onlineInfo.url
          this.teamsMeetingId = onlineInfo.meeting_id
          this.teamsMeetingPassword = onlineInfo.meeting_password
          this.onlineInfo.url = null
          this.onlineInfo.meetingId = null
          this.onlineInfo.meetingPassword = null
        }
        if (defaultInvitationIfo.microsoft_graph_event_id && this.hasQuicklyCreateMeetingPremium) {
          this.microsoftGraphEventInfo.oldMicrosoftGraphEventId = defaultInvitationIfo.microsoft_graph_event_id
          if (this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING) {
            this.showTeamsMeetingUrl = true
            this.oldTeamsMeetingInfo.meetingUrl = onlineInfo.url
            this.oldTeamsMeetingInfo.meetingId = onlineInfo.meeting_id
            this.oldTeamsMeetingInfo.meetingPassword = onlineInfo.meeting_password
            this.oldTeamsMeetingInfo.startDate = this.interviewTime.format('YYYY-MM-DD')
            this.oldTeamsMeetingInfo.startTime = this.interviewTime.format('HH:mm')
            this.oldTeamsMeetingInfo.endTime = moment(defaultInvitationIfo.interview_end_time).format('HH:mm')
            this.oldTeamsMeetingInfo.timezone = this.timeZone
          }
        }
      } else {
        await this.loadDefaultQuestionnaire()
      }
      if (!this.canUseAutomatedScheduler) {
        if (!this.isEditInterview || (!this.hasAutomatedSchedulerPremium && !this.isEditingGroupInterview)) {
          this.isSingleHm = false
        }
        this.isAutomate = false
      }
      if (this.hasCRMMicrositeCampaignPermission) {
        this.loadMicrositeList()
      }
      this.resetValidate()
      await this.$nextTick()
      await this.$refs.hmSelector.refreshForm(this.selectHiringList)
      await this.getHmSelectorData()
      this.selectedDate = moment().format('YYYY-MM-DD')
      this.stepLoading = false
    },
    async loadDefaultQuestionnaire () {
      const defaultQuestionnaireId = _.get(this.applicationList, '[0].job.default_interview_questionnaire_id')
      if (!defaultQuestionnaireId) {
        return
      }
      try {
        this.selectedQuestionnaire = await this.$refs.questionnaireSelectorDialog.loadQuestionnaireById(defaultQuestionnaireId)
      } catch (e) {
        this.selectedQuestionnaire = null
      }
    },
    async loadMicrositeList () {
      const resp = await this.searchMicrosite({ is_published: 1 })
      if (resp) {
        const url = `${window.location.origin}${MICROSITE_PATH.INDEX}`
        this.micrositeList = _.map(resp, item => {
          addI18nProperty(item.group, 'i18nName', 'cn_name', 'name')
          return {
            ...item,
            label: item.name,
            link: `${url}${item.short_unique_id}`
          }
        })
      }
    },
    async loadTimeZoneList () {
      const params = {
        lang: isEn() ? 'en' : 'zh-cn'
      }
      const resp = await this.getTimeZoneList(params)
      if (resp) {
        this.timeZoneList = resp.results
      }
    },
    async loadLocationList () {
      const params = {
        job_id: _.map(this.applicationList, item => item.job.id).join(',')
      }
      const resp = await this.getJobLocationList(params)
      if (resp) {
        this.locationList = _.map(resp, location => {
          Object.defineProperty(location, 'value', {
            configurable: true,
            enumerable: true,
            get () {
              const cityInfo = _.chain(['city', 'region', 'cbd'])
                .map(key => {
                  const val = location[key]
                  if (!val) {
                    return
                  }
                  return getI18nPropertyValue(val, 'cn_name', 'name')
                })
                .filter()
                .join(' ')
                .value() || ''
              return `${cityInfo}${cityInfo ? ' ' : ''}${location.address || ''}`
            }
          })
          return location
        })
      }
    },
    async loadInterviewMeetingRoomList () {
      const resp = await this.getInterviewMeetingRoomList()
      if (resp) {
        this.interviewMeetingRoomList = _.map(resp, room => {
          addI18nProperty(room, 'i18nName', 'cn_name', 'name')
          room.isBusy = false
          return room
        })
      }
    },
    async loadInterviewMeetingRoomScheduleList (startDate) {
      if (!this.hasViewHiringManagerAvailabilityPremium || _.isEmpty(this.interviewMeetingRoomList)) {
        return
      }

      let loadMore = true
      const tempStartDate = moment(startDate).format('YYYY-MM-DD HH:mm:ss')
      _.forEach(this.hasLoadMeetingRoomScheduleDateDurationList, item => {
        const start = item.start
        const end = item.end
        if (moment(tempStartDate).isSameOrAfter(moment(start)) && moment(tempStartDate).isSameOrBefore(moment(end))) {
          loadMore = false
        }
      })
      if (!loadMore) {
        return
      }

      let startTime
      let endTime
      if (this.isEditInterview) {
        this.editInterviewMeetingRoomLoadDataCount += 1
      }
      if (this.isEditInterview && this.editInterviewMeetingRoomLoadDataCount === 1) {
        startTime = moment().startOf('day')
        endTime = startDate ? moment(startDate).add(3, 'days').endOf('day') : moment(startTime).clone().add(3, 'days').endOf('day')
      } else {
        const start = startDate ? moment(startDate) : moment()
        startTime = moment(`${start.format('YYYY-MM-DD')} 00:00:00`, 'YYYY-MM-DD HH:mm:ss')
        endTime = moment(startTime).clone().add(3, 'days').endOf('day')
      }
      const meetingRoomEmailList = _.chain(this.interviewMeetingRoomList)
        .filter(item => item.email)
        .map('email')
        .value()
      if (_.isEmpty(meetingRoomEmailList)) {
        return
      }
      const params = {
        meeting_room_email_list: meetingRoomEmailList,
        start_date_time: startTime.format('YYYY-MM-DD HH:mm:ss'),
        end_date_time: endTime.format('YYYY-MM-DD HH:mm:ss'),
        availability_view_interval: 60,
        timezone: this.timeZone
      }
      this.stepLoading = true
      try {
        const resp = await this.getInterviewMeetingRoomSchedule(params)
        if (resp) {
          this.hasLoadMeetingRoomScheduleDateDurationList.push({
            start: startTime.format('YYYY-MM-DD HH:mm:ss'),
            end: endTime.format('YYYY-MM-DD HH:mm:ss')
          })
          _.forEach(resp, (item) => {
            const email = item.email
            const scheduleIndex = _.findIndex(this.meetingRoomScheduleList, { email })
            if (scheduleIndex !== -1) {
              item.event_list = _.concat(this.meetingRoomScheduleList[scheduleIndex].event_list, item.event_list)
              this.meetingRoomScheduleList.splice(scheduleIndex, 1, item)
            } else {
              this.meetingRoomScheduleList.push(item)
            }
          })
          this.calculateMeetingRoomSchedule()
        }
      } finally {
        this.stepLoading = false
      }
    },
    calculateMeetingRoomSchedule () {
      this.interviewMeetingRoomList = _.map(this.interviewMeetingRoomList, meetingRoom => {
        if (_.isEmpty(this.selectedDateTimeList)) {
          meetingRoom.isBusy = false
          return meetingRoom
        }
        const meetingRoomSchedule = _.find(this.meetingRoomScheduleList, { email: meetingRoom.email })
        if (!meetingRoomSchedule) {
          meetingRoom.isBusy = false
          return meetingRoom
        }
        const isBusy = _.some(this.selectedDateTimeList, (selectedDateTime) => {
          const currentTime = moment(selectedDateTime.dateTime)
          return _.some(meetingRoomSchedule.event_list, (busyTime) => {
            return busyTime.status !== 'free' &&
              this.isTimeRangeOverlap(currentTime, currentTime.clone().add(this.durationMinutes, 'minutes'), busyTime.start_time, busyTime.end_time)
          })
        })
        meetingRoom.isBusy = isBusy
        return meetingRoom
      })
    },
    isTimeRangeOverlap (start1, end1, start2, end2) {
      start1 = moment(start1)
      end1 = moment(end1)
      start2 = moment(start2)
      end2 = moment(end2)

      return end1.isAfter(start2) && start1.isBefore(end2)
    },
    verifyInterviewTimeOrHmChange () {
      const differenceHm = _.differenceBy(this.oldSelectHiringList, this.selectHiringList, 'email_address')
      const hmNoChange = this.oldSelectHiringList.length === this.selectHiringList.length && differenceHm.length === 0
      let timeChange = false
      if (this.selectedDateTimeList.length !== 1) {
        return true
      }
      const newInterviewTime = this.selectedDateTimeList[0].dateTime
      const startDate = moment(newInterviewTime).format('YYYY-MM-DD')
      const startTime = moment(newInterviewTime).format('HH:mm')
      const endTime = moment(newInterviewTime).add(this.durationMinutes, 'minutes').format('HH:mm')
      if (startDate !== this.oldTeamsMeetingInfo.startDate || this.timeZone !== this.oldTeamsMeetingInfo.timezone || startTime !== this.oldTeamsMeetingInfo.startTime || endTime !== this.oldTeamsMeetingInfo.endTime) {
        timeChange = true
      }
      return !hmNoChange || timeChange
    },
    resizeTeamsMeetingUrl () {
      if (this.interviewType !== INTERVIEW_TYPE_ONLINE_TEAMS_MEETING) {
        return
      }
      if (this.verifyInterviewTimeOrHmChange()) {
        this.teamsMeetingUrl = null
        this.teamsMeetingId = null
        this.teamsMeetingPassword = null
      } else {
        this.teamsMeetingUrl = this.oldTeamsMeetingInfo.meetingUrl
        this.teamsMeetingId = this.oldTeamsMeetingInfo.meetingId
        this.teamsMeetingPassword = this.oldTeamsMeetingInfo.meetingPassword
      }
    },
    handleDurationChange () {
      this.resizeTeamsMeetingUrl()
    },
    handleTimezoneChange () {
      this.hmScheduleList = []
      this.meetingRoomScheduleList = []
      this.hasLoadScheduleDateDurationList = []
      this.hasLoadMeetingRoomScheduleDateDurationList = []
      this.loadHmScheduleList(this.startDate)
      this.loadInterviewMeetingRoomScheduleList(this.startDate)
      this.resizeTeamsMeetingUrl()
    },
    getCurrentStepIndex (name) {
      return _.findIndex(this.stepList, item => item.name === name)
    },
    async handleNextClick () {
      // 校验选招聘经理
      if (this.getCurrentStepIndex(NAME_LIST.HM_SELECTOR) === this.activeIndex) {
        const isValid = this.$refs.hmSelector.handleNextClick()
        if (!isValid) {
          return
        }
        if (!this.validateGroupInterview()) {
          return
        }
      }

      if (this.getCurrentStepIndex(NAME_LIST.HM_SELECTOR) === this.activeIndex) {
        // 校验面试时间地点
        this.resetValidate()
        if (!this.isAutomatedSchedulerActive && _.isEmpty(this.selectedDateTimeList)) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.selectInterviewTime`))
          this.interviewTimeError = true
        } else if (this.selectedDateTimeList.length > 1) {
          let valid = true
          _.forEach(this.$refs.interviewDateTimeRange, (item) => {
            const itemValid = item.submit()
            if (!itemValid) {
              valid = false
            }
          })
          this.interviewTimeError = !this.checkSelectedDateTimeDuplicate() || !valid
        }
        // if (this.interviewType !== INTERVIEW_TYPE_ONLINE_TEAMS_MEETING && !this.interviewMeetingRoom) {
        //   this.interviewMeetingRoomError = this.$t('common.message.requiredFormField')
        // }
        if (this.interviewType === INTERVIEW_TYPE_F2F && !this.location) {
          this.locationError = this.$t('common.message.requiredFormField')
        }
        if (this.interviewType === INTERVIEW_TYPE_ONLINE && !this.onlineInfo.url) {
          this.urlError = this.$t('common.message.requiredFormField')
        }
        if (this.interviewType === INTERVIEW_TYPE_TELEPHONE && !this.telephone) {
          this.telephoneError = this.$t('common.message.requiredFormField')
        }

        if (this.interviewTimeError || this.locationError || this.interviewMeetingRoomError || this.urlError || this.telephoneError) {
          return
        }

        const needAuth = (this.hasQuicklyCreateMeetingPremium && !this.isAutomatedSchedulerActive) || this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING
        if (needAuth && !this.hasMicrosoftGraphAuthToken && this.selectedDateTimeList.length === 1) {
          await this.checkCurrentAccountAuthGraph()
          return
        }
      }

      if (this.getCurrentStepIndex(NAME_LIST.INTERVIEW_RATING_FORM) === this.activeIndex && !this.validateQuestionInterviewerAssignments()) {
        return
      }

      if (this.activeIndex < this.stepList.length - 1) {
        this.activeIndex = this.activeIndex + 1
      }
    },
    async getHmSelectorData () {
      if (this.hasViewHiringManagerAvailabilityPremium) {
        await this.checkCurrentAccountAuthGraph()
      } else if (this.hasQuicklyCreateMeetingPremium && !this.isAutomatedSchedulerActive) {
        await this.checkCurrentAccountAuthGraph()
      }
      await Promise.all([
        this.loadTimeZoneList(),
        this.loadLocationList(),
        this.loadInterviewMeetingRoomList(),
        this.loadHmScheduleList(this.startDate)
      ])
      await this.loadInterviewMeetingRoomScheduleList(this.startDate)
    },
    async handleSendClick () {
      this.needSaveMicrosoftOnlineMeeting = true
      if (!this.isAutomatedSchedulerActive &&
        this.selectedDateTimeList.length === 1 &&
        this.hasQuicklyCreateMeetingPremium &&
        !this.microsoftGraphEventInfo.newMicrosoftGraphEventId) {
        await this.createOrUpdateMicrosoftMeeting()
      }
      if (this.loading) {
        return
      }
      this.loading = true
      const availableInterviewTimeList = _.map(this.selectedDateTimeList, item => {
        const startTime = moment(item.time, 'HH:mm')
        return {
          startDate: item.date,
          startTime: startTime.clone().format('HH:mm:ss'),
          endTime: startTime.add(this.durationMinutes, 'minutes').format('HH:mm:ss')
        }
      })

      const hmInterviewSubject = this.hmSubjectContent
      const hmInterviewContent = this.hmTemplateContent

      const candidateInterviewSubject = this.candidateSubjectContent
      const candidateInterviewContent = this.candidateTemplateContent
      const isTeamsMeeting = this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING
      const isZoomMeeting = this.interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING
      const interviewType = isTeamsMeeting || isZoomMeeting ? INTERVIEW_TYPE_ONLINE : this.interviewType
      let meetingUrl = this.onlineInfo.url
      let meetingID = this.onlineInfo.meetingId
      let meetingPassword = this.onlineInfo.meetingPassword
      if (isTeamsMeeting) {
        meetingUrl = this.teamsMeetingUrl
        meetingID = this.teamsMeetingId
        meetingPassword = this.teamsMeetingPassword
      } else if (isZoomMeeting) {
        meetingUrl = this.zoomMeetingUrl
        meetingID = this.zoomMeetingId
        meetingPassword = this.zoomMeetingPassword
      }
      const invitation = {
        search_params: this.searchParams,
        is_all_application: this.isAllApplication,
        is_group_interview: this.isGroupInterview,
        group_name: this.isGroupInterview ? _.trim(this.groupInterviewName) : null,
        hire_managers: this.selectHiringList,
        comment: this.hmComment,
        interview_time_type: this.isAutomatedSchedulerActive && INTERVIEW_TIME_TYPE.AUTOMATIC,
        schedule: {
          datetime: null,
          datehours: null,
          timezone: this.timeZone,
          available_interview_time_list: availableInterviewTimeList,
          duration: {
            hour: this.duration.hour,
            min: this.duration.minute
          },
          location: {
            id: this.locationId,
            address: this.location?.value || this.location
          },
          interview_type: interviewType,
          online_info: {
            provider: isZoomMeeting ? 'zoom' : (isTeamsMeeting ? 'teams' : undefined),
            url: meetingUrl,
            address: this.location?.value || this.location,
            meeting_id: meetingID,
            meeting_password: meetingPassword
          },
          microsite: this.selectMicrosite ? this.selectMicrosite[1] : null,
          meeting_room_id: this.interviewMeetingRoom,
          telephone: this.telephone
        },
        email_template: {
          candidate: {
            subject: candidateInterviewSubject,
            content: candidateInterviewContent,
            language_code: this.sendToCandidateWithLanguage
          },
          hm: {
            subject: hmInterviewSubject,
            content: hmInterviewContent,
            language_code: this.sendToHmWithLanguage
          }
        },
        candidates: _.map(this.applicationList, item => item.candidate.id),
        job_id: this.applicationList[0].job.id, // Only the same job candidates can be invited
        send_hm_email: this.isSendToHmEmail,
        send_candidate_email: this.isSendToCandidateEmail,
        language_code: isEn() ? 'en' : 'zh-cn',
        is_teams_meeting: isTeamsMeeting,
        new_microsoft_graph_event_id: this.microsoftGraphEventInfo.newMicrosoftGraphEventId,
        old_microsoft_graph_event_id: this.microsoftGraphEventInfo.oldMicrosoftGraphEventId
      }
      if (this.selectedQuestionnaire) {
        invitation.questionnaire_id = this.selectedQuestionnaire.id
      }
      if (this.isGroupInterview) {
        invitation.question_hm_assignments = this.questionHmAssignments
      }

      try {
        if (this.saveWithEvent) {
          const promise = new Promise((resolve, reject) => {
            this.$emit('save', invitation, (close) => {
              if (close) {
                this.microsoftGraphEventInfo.newMicrosoftGraphEventId = null
                this.show = false
                resolve()
              }
            })
          })
          await promise
        } else {
          const resp = await this.sendInterviewInvitation(invitation)
          if (resp) {
            const success = resp.success
            const action = this.$t('common.btn.invite')
            if (success) {
              this.$message({
                message: this.$t('common.message.actionSuccess', { action }),
                type: 'success'
              })
              this.microsoftGraphEventInfo.newMicrosoftGraphEventId = null
              this.show = false
              this.$emit('sent', resp)
            } else {
              const statusName = this.applicationList[0].statusName
              this.$message({
                message: this.$t('common.message.inviteInterviewFail', { statusName }),
                type: 'error'
              })
            }
          }
        }
      } finally {
        this.loading = false
      }
    },
    handleHmListChange (hmList) {
      this.selectHiringList = hmList
      this.reconcileQuestionInterviewerAssignments()
      this.fillSingleHmQuestionInterviewerAssignments()
      if (_.isEmpty(this.selectHiringList)) {
        this.selectedDateTimeList = []
      }
      this.resizeTeamsMeetingUrl()
      this.hmScheduleList = []
      this.hasLoadScheduleDateDurationList = []
      this.loadHmScheduleList(this.selectedDate)
    },
    handleSelectHm (hm) {
      if (this.isAutomatedSchedulerActive) {
        return
      }
      this.showInterviewSchedule = true
      const hmList = [{
        id: hm.id,
        company: hm.company.id,
        email: hm.email_address,
        first_name: hm.first_name,
        last_name: hm.last_name
      }]
      this.hmScheduleList = []
      this.hasLoadScheduleDateDurationList = []
      this.loadHmScheduleList(this.selectedDate, null, null, hmList)
    },
    loadHmScheduleListPre () {
      const hmEmail = this.$refs.hmSelector.hmEmail
      const hmEmailList = this.$refs.hmSelector.hmEmailList
      const hm = _.find(hmEmailList, { email_address: hmEmail })
      const hmList = [{
        id: hm.id,
        company: hm.company.id,
        email: hm.email_address,
        first_name: hm.first_name,
        last_name: hm.last_name
      }]
      this.showInterviewSchedule = true
      this.hmScheduleList = []
      this.hasLoadScheduleDateDurationList = []
      this.loadHmScheduleList(this.selectedDate, null, null, hmList)
    },
    async handleCancel () {
      this.activeIndex = this.activeIndex - 1
      await this.deleteMicrosoftMeeting()
    },
    async handleClosedDialog () {
      await this.deleteMicrosoftMeeting()
      this.startDate = null
      this.startTime = null
      this.endTime = null
      this.invalidateEmailTemplateLoad()
      this.handleEnableOperateBtn()
    },
    handleInterviewerBtnClick () {
      this.$refs.hmSelector.handleSelectCurrentAccount()
    },
    handleSave () {
      const isLastStep = this.activeIndex === this.stepList.length - 1
      if (isLastStep) {
        this.showPreviewInterviewEmailDialog = false
        this.handleSendClick()
      } else {
        this.handleNextClick()
      }
    },
    getAddressEmailContent (lang) {
      if (this.interviewType === INTERVIEW_TYPE_F2F) {
        return this.$t(`${this.i18nPrefix}.emailTemplate.addressContent`, lang)
      }
      let result = this.$t(`${this.i18nPrefix}.emailTemplate.onlineAddress.url`, lang)
      let meetingId = this.onlineInfo.meetingId
      let meetingPassword = this.onlineInfo.meetingPassword
      if (this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING) {
        meetingId = this.teamsMeetingId
        meetingPassword = this.teamsMeetingPassword
      } else if (this.interviewType === INTERVIEW_TYPE_ONLINE_ZOOM_MEETING) {
        meetingId = this.zoomMeetingId
        meetingPassword = this.zoomMeetingPassword
      }
      if (meetingId) {
        result += this.$t(`${this.i18nPrefix}.emailTemplate.onlineAddress.meetingId`, lang)
      }
      if (meetingPassword) {
        result += this.$t(`${this.i18nPrefix}.emailTemplate.onlineAddress.password`, lang)
      }
      result += '\n'
      return result
    },
    async checkCurrentAccountAuthGraph () {
      if (this.hasMicrosoftGraphAuthToken || (this.microsoftGraphAuthWin && this.microsoftGraphAuthWin.closed)) {
        this.microsoftGraphAuthWin = null
        this.microsoftGraphAuthUrl = null
        return
      }
      const resp = await this.checkMicrosoftGraphAuth()
      if (resp) {
        this.hasMicrosoftGraphAuthToken = false
        if (!this.microsoftGraphAuthUrl) {
          this.microsoftGraphAuthUrl = resp
          this.microsoftGraphAuthWin = openNewBrowserWindowByUrl(this.microsoftGraphAuthUrl, null, 500, 600)
        }
        setTimeout(this.checkCurrentAccountAuthGraph, 1000)
      } else {
        this.microsoftGraphAuthUrl = null
        this.hasMicrosoftGraphAuthToken = true
        if (this.microsoftGraphAuthWin) {
          this.microsoftGraphAuthWin.close()
        }
      }
    },
    async deleteMicrosoftMeeting () {
      if (!this.hasQuicklyCreateMeetingPremium || !this.needSaveMicrosoftOnlineMeeting) {
        return
      }
      let needCancelEventId
      if (this.microsoftGraphEventInfo.oldMicrosoftGraphEventId) {
        if (this.verifyInterviewTimeOrHmChange()) {
          needCancelEventId = this.microsoftGraphEventInfo.newMicrosoftGraphEventId || this.microsoftGraphEventInfo.oldMicrosoftGraphEventId
        } else if (this.interviewType !== INTERVIEW_TYPE_ONLINE_TEAMS_MEETING) {
          needCancelEventId = this.microsoftGraphEventInfo.oldMicrosoftGraphEventId
        }
      } else {
        needCancelEventId = this.microsoftGraphEventInfo.newMicrosoftGraphEventId
      }
      if (needCancelEventId) {
        if (!this.hasMicrosoftGraphAuthToken) {
          await this.checkCurrentAccountAuthGraph()
        }
        await this.deleteOnlineMeetingEvent({ eventId: needCancelEventId })
      }
    },
    async createOrUpdateMicrosoftMeeting () {
      if (!this.hasMicrosoftGraphAuthToken) {
        await this.checkCurrentAccountAuthGraph()
      }

      const dateTime = this.selectedDateTimeList[0].dateTime
      const startDateTime = dateTime
      const endDateTime = moment(dateTime).add(this.durationMinutes, 'minutes').format('YYYY-MM-DD HH:mm:ss')
      const selectedMeetingRoom = _.find(this.interviewMeetingRoomList, { id: this.interviewMeetingRoom })
      let meetingRoomEmail = null
      if (selectedMeetingRoom) {
        meetingRoomEmail = selectedMeetingRoom.email
      }

      const isOnlineMeeting = this.interviewType === INTERVIEW_TYPE_ONLINE_TEAMS_MEETING
      const params = {
        start_date_time: startDateTime,
        end_date_time: endDateTime,
        timezone: this.timeZone,
        hire_managers: this.selectHiringList,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id),
        meeting_room_email: meetingRoomEmail,
        job_id: this.applicationList[0].job.id, // Only the same job candidates can be invited
        is_online_meeting: isOnlineMeeting
      }
      let resp = null
      this.loading = true
      try {
        if (this.microsoftGraphEventInfo.newMicrosoftGraphEventId) {
          await this.deleteOnlineMeetingEvent({ eventId: this.microsoftGraphEventInfo.newMicrosoftGraphEventId })
        }
        if (this.microsoftGraphEventInfo.oldMicrosoftGraphEventId) {
          params.microsoft_graph_event_id = this.microsoftGraphEventInfo.oldMicrosoftGraphEventId
          resp = await this.updateOnlineMeetingEvent(params)
          this.microsoftGraphEventInfo.oldMicrosoftGraphEventId = null
        } else {
          resp = await this.createOnlineMeetingEvent(params)
        }
        if (resp) {
          const eventId = resp.id
          this.microsoftGraphEventInfo.newMicrosoftGraphEventId = eventId
          const meetingInfo = resp.meeting_info
          if (meetingInfo) {
            this.teamsMeetingUrl = meetingInfo.online_meeting?.joinUrl || ''
            this.teamsMeetingId = meetingInfo.meeting_id || ''
            this.teamsMeetingPassword = meetingInfo.passcode || ''
          }
        } else {
          return
        }
      } finally {
        this.loading = false
      }
    },
    async handleCreateMsMeeting () {
      this.showTeamsMeetingUrl = !this.showTeamsMeetingUrl
      if (this.showTeamsMeetingUrl) {
        this.interviewType = INTERVIEW_TYPE_ONLINE_TEAMS_MEETING
      } else {
        this.interviewType = INTERVIEW_TYPE_ONLINE
      }
      this.checkCurrentAccountAuthGraph()
    },
    handleInterviewTypeChange (interviewType) {
      const interviewTypeInfo = _.find(this.interviewTypeList, { key: interviewType })
      if (interviewTypeInfo && interviewTypeInfo.handle) {
        interviewTypeInfo.handle()
      }
      this.resetValidate()
    },
    resetValidate () {
      this.interviewTimeError = null
      this.interviewMeetingRoomError = null
      this.locationError = null
      this.telephoneError = null
      this.urlError = null
    },
    resetTinymceDialogStyle () {
      // 由于插入 AI Summary 内容导致 tinymce 渲染在 body 上要等待 AI Summary 内容插入
      setTimeout(() => {
        const tinymceDialogs = document.querySelectorAll('.tox-tinymce-aux')
        if (tinymceDialogs && tinymceDialogs.length > 0) {
          _.forEach(tinymceDialogs, dialog => {
            dialog.style.zIndex = '99999999'
          })
        }
      }, 500)
    },
    handleSendHmLanguageChange () {
      if (!this.showHmEmailTemplate) {
        return
      }
      this.showHmEmailTemplate = false
      this.$nextTick(() => {
        if (!this.loadingHmTemplate) {
          this.showHmEmailTemplate = true
          this.resetTinymceDialogStyle()
        }
      })
    },
    handleGenerateAISummary (candidateId, jobId, languageCode, callback) {
      const application = _.find(this.applicationList, item => item.candidate.id === candidateId)
      const aiTask = {
        task: null,
        isFinished: false
      }
      let aiSummary = ''
      this.disabledHmOperateBtn = true
      aiTask.task = new AsyncTask(() => this.createAISummary({
        candidate_id: application.candidate.id,
        job_id: jobId,
        language_code: languageCode
      }), (taskId) => this.getAISummaryInfoByTaskId(taskId), (taskId, resp) => {
        let aiSummaryInfo
        if (resp) {
          aiSummaryInfo = resp.content
          aiSummary = languageCode === 'en' ? aiSummaryInfo.ai_summary_en : aiSummaryInfo.ai_summary_cn
          aiTask.isFinished = true
        }
        const content = {
          candidate_id: application.candidate.id,
          job_id: application.job.id,
          candidate_name: application.candidate.name,
          summary: aiSummary
        }
        if (aiSummaryInfo) {
          application.candidate.ai_summary_info = aiSummaryInfo
        }
        callback(content)
      })
    },
    handleGenerateAIMatch (candidateId, jobId, languageCode, callback) {
      const application = _.find(this.applicationList, item => item.candidate.id === candidateId)
      const aiTask = {
        task: null,
        isFinished: false
      }
      let aiMatchInfo = {}
      this.disabledHmOperateBtn = true
      aiTask.task = new AsyncTask(() => this.createAIResumeMatch({
        candidate_ids: [application.candidate.id],
        job_id: jobId,
        language_code: languageCode,
        await_result: true
      }), (taskId) => this.getAIMatchInfoByTaskId(taskId), (taskId, resp) => {
        if (resp) {
          aiTask.isFinished = true
          const result = resp.result
          const key = `${jobId}_${application.candidate.id}`
          aiMatchInfo = _.get(result, key)
          application.candidate.ai_match_info = aiMatchInfo
        }

        callback(aiMatchInfo)
      })
    },
    handleEnableOperateBtn () {
      this.disabledHmOperateBtn = false
    },
    async handleSingleHmChange (value) {
      if (value && this.selectHiringList.length > 1) {
        this.handleHmListChange([])
        await this.$refs.hmSelector.refreshForm(this.selectHiringList)
      }
      this.isAutomate = this.hasAutomatedSchedulerPremium && !this.isAllApplication && this.applicationList.length === 1 && value
    },
    handleLocationChange () {
      this.locationError = null
    },
    handleInterviewMeetingRoomChange () {
      this.interviewMeetingRoomError = null
      if (this.interviewType === INTERVIEW_TYPE_F2F) {
        const meetingRoom = _.find(this.interviewMeetingRoomList, { id: this.interviewMeetingRoom })
        this.location = meetingRoom?.address
      }
    },
    handleClearInterviewMeetingRoom () {
      this.interviewMeetingRoom = null
    }
  },
  watch: {
    selectedDate: {
      handler (value) {
        this.loadHmScheduleList(value)
        this.loadInterviewMeetingRoomScheduleList(value)
      }
    },
    async activeIndex () {
      if (this.stepList[this.activeIndex].name === NAME_LIST.EMAIL_EDITOR) {
        this.sendToHmWithLanguage = this.$i18n.locale === 'en' ? 'en' : 'zh-cn'
        this.sendToCandidateWithLanguage = this.$i18n.locale === 'en' ? 'en' : 'zh-cn'
        await this.prepareEmailTemplates()
      } else {
        this.invalidateEmailTemplateLoad()
      }
    },
    isSendToHmEmail () {
      this.previewInterviewEmailType = this.isSendToHmEmail ? 0 : 1
    },
    isSingleHm (value) {
      if (this.$refs.hmSelector.hmEmail && !this.isAutomatedSchedulerActive) {
        this.loadHmScheduleListPre()
      }
    },
    isAutomate (value) {
      if (value) {
        this.showInterviewSchedule = false
      } else {
        if (this.$refs.hmSelector.hmEmail) {
          this.loadHmScheduleListPre()
        }
      }
    },
    isGroupInterview (value) {
      if (!value) {
        this.groupInterviewNameError = false
        this.showInterviewerAssignmentErrors = false
      } else {
        this.fillSingleHmQuestionInterviewerAssignments()
      }
    }
  }
}
</script>

<style lang='scss' scoped>
.invite-hm-interview-dialog {
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ::v-deep {
    .el-dialog {
      width: 60%;
      min-width: 850px;
    }

    .el-dialog__body {
      padding-left: 36px;
      padding-right: 46px;
    }
  }

  .hm-interview-form {
    word-break: initial;

    .step-list {
      ::v-deep {
        .step-container {
          padding: 14px 20px;

          .step {
            .el-step__title {
              font-size: 14px;
              color: #616266;

              &.is-process {
                font-weight: 500;
                color: #0096cc;
              }
            }

            .el-step__arrow {
              &:before {
                height: 12px;
                transform: rotate(-45deg) translateY(-3px);
              }

              &:after {
                height: 12px;
              }
            }
          }
        }
      }
    }

    .step-content {
      margin-top: 25px;

      .hm-comments {
        .content {
          margin-top: 16px;
        }
      }

      .questionnaire-step {
        .questionnaire-actions {
          display: flex;
          margin-top: 16px;

          .questionnaire-action-btn {
            flex: 1;
            height: 39px;
            margin-left: 10px;
            border-radius: 4px;
            border-color: #c0c4cc;
            background: #0096cc;
            color: #ffffff;

            &:first-child {
              margin-left: 0;
            }
          }
        }

        .selected-questionnaire-preview {
          margin-top: 16px;
        }
      }

      .chose-template-btn {
        margin-top: 16px;
        display: flex;

        .template-btn {
          background: #0096CC;
          color: #FFFFFF;
          flex: 1;
        }

        .crate-btn {
          background: #0096CC;
          color: #FFFFFF;
          flex: 1;
        }
      }

      .question-list {
        max-height: 250px;
        margin-top: 16px;
        overflow-y: auto;

        .question {
          display: flex;
          align-items: center;

          .question-order {
            color: #0096CC;
            font-weight: bold;
            font-size: 20px;
            display: table-cell;
            vertical-align: middle;
            padding-bottom: 15px;
            padding-right: 7px;

            .sloping-side {
              height: 0px;
              border-bottom: 1px solid #0096CC;
              width: 24px;
              transform: rotate(121deg);
              margin-top: -2px;
              margin-left: 1px;
            }
          }

          .question-content-wrapper {
            flex: 1;
            padding: 12px 37px 0 20px;
            background: #f7f7f7;
            position: relative;

            .question-content {
              padding-right: 80px;
              padding-bottom: 12px;
              border-bottom: 1px solid #dfdfdf;
            }

            .question-operation {
              width: 80px;
              position: absolute;
              top: 12px;
              right: 0px;
            }
          }

          &:last-child {
            .question-content-wrapper {
              .question-content {
                border-bottom-color: transparent;
              }
            }
          }
        }
      }

      .question-operation {
        display: flex;
        justify-content: flex-end;

        .edit-btn {
          display: inline-block;
          margin-right: 0;
          padding: 0 5px;
          cursor: pointer;
          font-size: 16px;

          &.drag {
            cursor: grab;
            color: #000000;

            i {
              font-size: 18px;
            }
          }

          &.edit {
            color: #0096cc;
          }

          &.delete {
            color: #fb4646;
          }
        }
      }

      .question-input {
        margin-top: 16px;

        .question-operation {
          margin-bottom: 10px;
        }
      }

      .content {
        .interview-model {
          border-bottom: 1px solid #dcdfe6;
          display: grid;
          grid-template-columns: minmax(0, 355fr) minmax(0, 403fr);
          gap: 20px 16px;
          padding-bottom: 20px;

          .interview-model-panel {
            box-sizing: border-box;
            min-height: 92px;
            border-radius: 4px;
            border: 1px solid #dcdfe6;
            padding: 20px 16px;
          }

          .hm-model {
            .el-radio-group {
              display: flex;
              gap: 28px;
              margin-top: 16px;

              .el-radio {
                margin-right: 0;
                color: #606266;

                ::v-deep {
                  .el-radio__label {
                    padding-left: 10px;
                    font-weight: 400;
                  }
                }

                &.is-checked:not(.is-disabled) {
                  ::v-deep {
                    .el-radio__label {
                      color: #606266;
                    }
                  }
                }
              }
            }
          }

          .interview-type {
            padding: 20px 16px 10px 16px;

            .interview-type-content {
              display: flex;
              align-items: center;
              margin-top: 16px;
              height: 30px;

              .el-radio-group {
                display: flex;
                flex-shrink: 0;
                gap: 15px;

                .el-radio {
                  margin-right: 0;
                  color: #606266;

                  ::v-deep {
                    .el-radio__label {
                      padding-left: 10px;
                      font-weight: 400;
                    }
                  }

                  &.is-checked:not(.is-disabled) {
                    ::v-deep {
                      .el-radio__label {
                        color: #606266;
                      }
                    }
                  }
                }
              }

              .group-name-input {
                min-width: 0;
                margin-left: 8px;

                ::v-deep {
                  .el-input__inner {
                    height: 30px;
                    line-height: 30px;
                    border-color: #c0c4cc;
                    border-radius: 2px;
                  }
                }

                &.is-error {
                  ::v-deep {
                    .el-input__inner {
                      border-color: #f56c6c;
                    }
                  }
                }
              }
            }
          }

          .automate {
            &.is-full-row {
              grid-column: 1 / -1;
            }

            .el-checkbox {
              margin-top: 16px;

              ::v-deep {
                .el-checkbox__label {
                  font-weight: 400;
                }
              }

              &.is-checked {
                ::v-deep {
                  .el-checkbox__label {
                    color: #000;
                  }
                }
              }
            }
          }

          .title {
            line-height: 14px;
            font-size: 14px;
            font-weight: 500;
            color: #303133;
          }
        }

        .hm {
          margin-top: 20px;
          border-bottom: 1px solid rgba(0,0,0,0.2);

          .hm-selector {
            width: 100%;
            padding-bottom: 20px;

            ::v-deep {
              .selector-content {
                margin-bottom: 0;

                .hm-email {
                  .el-select {
                    width: 100%;
                  }
                }

                .hm-last-name {
                  margin-right: 15px;
                }

                &.has-hm-email-error {
                  margin-bottom: 36px;
                }
              }

              .hm-list {
                font-size: 14px;
                margin-top: 20px;

                .hm {
                  height: 20px;
                  color: #666;
                  margin-bottom: 10px;
                }
              }

              .add-hm-btn, .delete-hm-btn {
                .icon-btn {
                  font-size: 20px;
                }
              }

              .el-input__inner {
                height: 30px;
                line-height: 30px;
              }

              .el-input__icon {
                line-height: 30px;
              }
            }
          }
        }

        .time {
          margin-top: 20px;
          border-bottom: 1px solid rgba(0,0,0,0.2);

          .time-type-content {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;

            .time-zone {
              display: flex;
              align-items: center;

              .label {
                font-size: 12px;
                margin-right: 10px;
                color: #000;
              }

              .time-zone-selector {
                width: 292px;

                ::v-deep {
                  .el-input__inner {
                    color: #000;
                    font-size: 12px;
                    border-radius: 2px;
                  }
                }
              }
            }
          }

          .time-schedule-content {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
          }

          .time-range-content {
            margin-bottom: 20px;

            .interview-time-range {
              & + .interview-time-range {
                margin-top: 10px;
              }
            }
          }
        }

        .location {
          display: flex;
          flex-direction: column;
          margin-top: 20px;

          .title {
            margin-bottom: 14px;
          }

          .location-select {
            position: relative;
            flex: 1;

            ::v-deep .el-radio-group {
              margin-bottom: 6px;

              .el-radio-button__inner {
                padding: 8px 20px;
                font-weight: 400;
                color: #000;
              }

              .is-active {
                .el-radio-button__inner {
                  color: #0096cc;
                  background: rgba(0, 150, 204, 0.1);
                  border-color: #c0c4cc;
                  box-shadow: initial;
                }
              }
            }

            .telephone-tip {
              color: #999;
              font-size: 12px;
              line-height: 18px;
            }

            .el-input {
              margin-top: 10px;
            }

            .el-select {
              margin-top: 10px;
            }

            .error {
              padding-top: 4px;
              color: #f56c6c;
              font-size: 12px;
              line-height: 1;
            }

            .teams-meeting {
              margin-top: 6px;
              font-size: 12px;
              color: #999;
              line-height: 18px;

              &.has-teams-url {
                margin-top: 0;
              }
            }
          }
        }

        .content-title {
          line-height: 20px;
          font-size: 14px;
          color: #000;
          font-weight: 500;
          margin-bottom: 10px;

          .icon-tip {
            vertical-align: middle;
            color: #C2C5CB;
            font-size: 14px;
          }
        }
      }

      .invitation {
        margin-top: 10px;

        .label {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .to-select-language {
            display: flex;
            align-items: center;
            .send-type {
              display: block;
              margin-right: 14px;
            }

            .language-title {
              display: block;
              margin-right: 14px;
            }

            .language-select {
              flex: 1;
            }
          }
        }

        .invitation-subject {
          display: flex;
          align-items: center;
          margin-top: 16px;
          margin-bottom: 16px;

          .subject-label {
            width: 80px;
            font-size: 18px;
            font-weight: 400;
            color: #606266;
          }
          ::v-deep {
            .el-input {
              flex: 1;
              margin-left: 12px;
              width: auto;
            }
          }
        }

        .invitation-content{
          padding-left: 92px;
        }
      }
    }

    .content-label {
      margin-bottom: 10px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }

    .microsite-select-container {
      display: flex;
      align-items: center;

      .microsite-label {
        width: 80px;
        font-size: 18px;
        font-weight: 400;
        color: #606266;
      }

      .microsite-select {
        flex: 1;
        margin-left: 12px;
      }
    }
  }

  .dialog-footer {
    .footer-btn  {
      border-radius: 2px;
      padding: 10px 30px;
    }

    .save-btn {
      background: #0096cc;
      border-color: #0096cc;
      color: #ffffff;
    }

    .cancel-btn {
      background: #ffffff;
      color: #606266;
    }
  }
}

.preview-interview-email-dialog {
  overflow: hidden;

  ::v-deep {
    .el-dialog {
      margin-top: 0;
      height: 100%;
      overflow: hidden;

      .el-dialog__body {
        height: 94%;
        overflow: hidden;
        word-break: break-word;
        white-space: normal;
      }
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .select-preview-type {
      display: inline-block;
      width: 12%;
    }

    .operation-btn {
      display: inline-block;
      .footer-btn  {
        border-radius: 2px;
        padding: 10px 30px;
      }

      .save-btn {
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }

      .cancel-btn {
        background: #ffffff;
        color: #606266;
      }

    }
  }

  .preview-content {
    margin-top: 10px;
    min-height: 500px;
    overflow-y: auto;
    max-height: 90%;
    ::v-deep {
      #custom-tag-candidate_ai_summary {
        .candidate-ai-summary {
          .candidate-summary-item {
            .name {
              cursor: not-allowed !important;
              a {
                cursor: not-allowed;
                pointer-events: none;
              }
            }
            .rate-btn {
              cursor: not-allowed !important;
              a {
                cursor: not-allowed;
                pointer-events: none;
              }
            }
          }
        }
        .view-dashboard-btn {
          cursor: not-allowed !important;
          a {
            cursor: not-allowed;
            pointer-events: none;
          }
        }
      }
    }
  }
}

.hm-status {
  font-size: 18px;
  left: 10px;
  position: absolute;
  display: inline-block;
  color: #9ad792;

  &.is-busy {
    color: #e76f78;
  }

  & + .hm-label {
    padding-left: 10px;
  }
}

.meeting-room-options {
  .el-select-dropdown__item {
    padding-left: 12px;

    .time-icon {
      margin-right: 8px;
      color: #98d49a;

      &.is-busy {
        color: #e76f78;
      }
    }
  }
}

.page-en {
  .invite-hm-interview-dialog {
    .hm-interview-form {
      .step-list {
        ::v-deep {
          .step-container {
            .step {
              &:first-child {
                flex-basis: 16% !important;
              }
            }
          }
        }
      }
    }
  }
}
</style>
